import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import styled from "styled-components";
import {
  Avatar,
  Button,
  Input,
  message,
  Badge,
  Empty,
  Row,
  Col,
  Statistic,
  Spin,
  Switch,
  Alert,
} from "antd";
import {
  MessageOutlined,
  UserOutlined,
  SendOutlined,
  HomeOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
  MenuOutlined,
  BellOutlined,
  BellFilled,
  InfoCircleOutlined,
  TeamOutlined,
  ToolOutlined,
  LoadingOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../helpers/firebase";
import { AuthContext } from "../context/AuthProvider";
import { isLimitedToOpsChatExperience } from "../helpers/accountProduct";
import { listTenants, getTenant } from "../lib/firebase/tenantModel";
import { chatModel } from "../lib/firebase/chatModel";
import unitModel from "../lib/firebase/unitModel";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import { getLeaseEndDate } from "../lib/leaseUtils";
import HandymanModel from "../lib/firebase/handymanModel";
import { propertyKeys, chatKeys } from "../lib/queryKeys";
import { trackEvent } from "../helpers/mixpanel";
import { device, useBreakpoints } from "../lib/breakpoints";
import logoImage from "../assets/logo.png";
import pluralize from "pluralize";
import vendorConversationAPI from "../lib/api/vendorConversationAPI";
import HealthConsentModal from "../components/compliance/HealthConsentModal";
import {
  AttachButton,
  renderLeoMarkdown,
} from "../components/chat/tenantChatSharedStyles";

const { TextArea } = Input;

// Helper function to convert number to ordinal
const getOrdinalSuffix = (num) => {
  if (!num || isNaN(num)) return "";

  const j = num % 10;
  const k = num % 100;

  if (j === 1 && k !== 11) {
    return num + "st";
  }
  if (j === 2 && k !== 12) {
    return num + "nd";
  }
  if (j === 3 && k !== 13) {
    return num + "rd";
  }
  return num + "th";
};

// Helper function to format dates from various sources
const formatDate = (dateValue) => {
  if (!dateValue) return "N/A";

  try {
    // Handle Firestore Timestamp objects
    if (dateValue && typeof dateValue === "object" && "toDate" in dateValue) {
      return dateValue.toDate().toLocaleDateString();
    }
    // Handle JavaScript Date objects
    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString();
    }
    // Handle string dates
    if (typeof dateValue === "string") {
      return new Date(dateValue).toLocaleDateString();
    }
    // Handle numeric timestamps
    if (typeof dateValue === "number") {
      return new Date(dateValue).toLocaleDateString();
    }

    return "N/A";
  } catch (error) {
    console.error("Error formatting date:", error, "Value:", dateValue);
    return "N/A";
  }
};

const Communication = () => {
  const { isMobile } = useBreakpoints();
  const { user, userInfo } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const location = useLocation();

  const [selectedChat, setSelectedChat] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [chats, setChats] = useState([]);
  const [allChats, setAllChats] = useState([]); // Store all chats
  const [pendingMessages, setPendingMessages] = useState(new Map()); // Track messages being sent: Map<tempId, {message, chatId, isSending}>
  const [healthConsentModalVisible, setHealthConsentModalVisible] = useState(
    false
  );
  const [isInitializingChats, setIsInitializingChats] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(
    "default"
  );
  const hasSelectedFromUrlRef = useRef(false);
  const chatsInitializedRef = useRef(false);
  const initialTenantIdRef = useRef(null);
  const [showDetailedInfo, setShowDetailedInfo] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const sidebarRef = useRef(null);
  const mainChatAreaRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const lastIsAtBottomRef = useRef(true);
  const prevMessagesCountRef = useRef(0);
  const unsubscribeRef = useRef(null);
  const [repairIdToScrollTo, setRepairIdToScrollTo] = useState(null);
  const [publicLeoPendingImages, setPublicLeoPendingImages] = useState([]);
  const [publicLeoImageUploading, setPublicLeoImageUploading] = useState(false);
  const publicLeoFileInputRef = useRef(null);

  // Phone number formatting utility
  const formatPhoneNumber = (value) => {
    if (!value) return "";

    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, "");

    // Don't format if empty or too short
    if (phoneNumber.length === 0) return "";
    if (phoneNumber.length < 4) return phoneNumber;

    // Format based on length
    if (phoneNumber.length < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length < 11) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6
      )}-${phoneNumber.slice(6)}`;
    } else {
      // Handle longer numbers (with country code)
      return `+${phoneNumber.slice(
        0,
        phoneNumber.length - 10
      )} (${phoneNumber.slice(-10, -7)}) ${phoneNumber.slice(
        -7,
        -4
      )}-${phoneNumber.slice(-4)}`;
    }
  };

  // Security check: Verify if user has access to a specific tenant's chat
  const hasAccessToTenant = useCallback(
    (tenant, units) => {
      if (!tenant || !units || !user) {
        return false;
      }

      // Find the unit for this tenant - check multiple possible field names
      const tenantUnit = units.find(
        (unit) =>
          unit.id === tenant.unitId ||
          unit.id === tenant.propertyId ||
          unit.unitNumber === tenant.unitNumber
      );

      if (!tenantUnit) {
        return false;
      }

      // Check if the current user is the owner of this unit
      return tenantUnit.ownerId === user.uid;
    },
    [user]
  );

  // Initialize sidebar state based on mobile and URL parameters
  useEffect(() => {
    if (isMobile) {
      // On mobile, show sidebar by default unless there's a specific tenantId in URL
      setIsSidebarOpen(!tenantId);
    } else {
      // On desktop, always show sidebar
      setIsSidebarOpen(true);
    }
  }, [isMobile, tenantId]);

  // Load tenants data
  const { data: tenantsData, isLoading: isTenantsLoading } = useQuery({
    queryKey: propertyKeys.tenants(user?.uid),
    queryFn: () => listTenants(),
    enabled: !!user?.uid,
  });

  // Load units data
  const { data: units = [], isLoading: isUnitsLoading } = useQuery({
    queryKey: propertyKeys.units(user?.uid),
    queryFn: () => unitModel.getByOwner(user.uid),
    enabled: !!user?.uid,
  });

  // Helper function to get unit address
  const getUnitAddress = useCallback(
    (tenantData) => {
      if (!tenantData?.unitId || !units.length) return "Address not available";

      const unit = units.find((u) => u.id === tenantData.unitId);
      if (!unit) return "Address not available";

      return getUnitDisplayAddress(unit) || "Address not available";
    },
    [units]
  );

  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return "Just now";

    try {
      let date;
      if (timestamp instanceof Date) {
        date = timestamp;
      } else if (
        timestamp &&
        typeof timestamp === "object" &&
        "toDate" in timestamp
      ) {
        // Handle Firestore Timestamp
        date = timestamp.toDate();
      } else {
        date = new Date(timestamp);
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn("Invalid timestamp:", timestamp);
        return "Just now";
      }

      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);

      if (diffInHours < 1) {
        return "Just now";
      } else if (diffInHours < 24) {
        return `${Math.floor(diffInHours)} ${pluralize(
          "hour",
          Math.floor(diffInHours)
        )} ago`;
      } else {
        return date.toLocaleDateString();
      }
    } catch (error) {
      console.error("Error formatting time:", error);
      return "Just now";
    }
  }, []);

  // Load individual tenant data when a chat is selected
  const {
    data: selectedTenantData,
    isLoading: isSelectedTenantLoading,
  } = useQuery({
    queryKey: ["tenant", selectedChat?.tenantId, units.length],
    queryFn: async () => {
      const tenantData = await getTenant(selectedChat.tenantId);

      // Add unit address to tenant data
      const unitAddress = getUnitAddress(tenantData);

      return {
        ...tenantData,
        unitAddress,
        leaseEnd: getLeaseEndDate(tenantData),
      };
    },
    enabled: !!selectedChat?.tenantId && units.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  // Load messages for selected chat using React Query
  // Magic-link Leo transcripts use publicLeoChatSessions (not Firestore chats / not backend conversations)
  const isPublicLeoSession = selectedChat?._isPublicLeoSession === true;
  const isBackendConversation =
    selectedChat?._fromBackend === true && !isPublicLeoSession;

  // Log when selectedChat changes to debug _fromBackend flag
  useEffect(() => {
    if (selectedChat) {
      console.log("[Communication] Selected chat changed:", {
        chatId: selectedChat.id,
        tenantId: selectedChat.tenantId,
        tenantName: selectedChat.tenantName,
        _fromBackend: selectedChat._fromBackend,
        isBackendConversation,
        hasVendors: selectedChat.participants?.some(
          (p) => p.userType === "vendor" || p.userType === "handyman"
        ),
        vendorCount:
          selectedChat.participants?.filter(
            (p) => p.userType === "vendor" || p.userType === "handyman"
          ).length || 0,
      });
    }
  }, [selectedChat, isBackendConversation]);

  // Fetch full conversation data for backend conversations (to get participants and disabledParticipants)
  const {
    data: backendConversationData,
    isLoading: isLoadingBackendConversation,
    refetch: refetchConversation,
  } = useQuery({
    queryKey: ["backendConversationData", selectedChat?.id, user?.uid],
    queryFn: async () => {
      if (!isBackendConversation || !selectedChat?.id || !user?.uid)
        return null;

      const API_URL =
        process.env.REACT_APP_API_URL || "http://localhost:4000/api";
      if (!API_URL) {
        throw new Error("REACT_APP_API_URL not configured");
      }

      // Always pass userId for proper filtering
      console.log("[Communication] Fetching backend conversation:", {
        conversationId: selectedChat.id,
        landlordId: user.uid,
        chatType: selectedChat.type,
        isBackendConversation: selectedChat._fromBackend,
        tenantId: selectedChat.tenantId,
        url: `${API_URL}/conversations/${selectedChat.id}?userId=${user.uid}`,
      });

      // Request all messages with includeAllMessages=true to ensure we get complete history
      const response = await fetch(
        `${API_URL}/conversations/${selectedChat.id}?userId=${user.uid}&limit=1000&includeAllMessages=true`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 404) {
        // Conversation not found or user is disabled
        console.warn(
          "[Communication] Conversation not found (404):",
          selectedChat.id
        );
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch conversation: ${response.status}`);
      }

      const data = await response.json();
      const conversation = data.success ? data.conversation : null;

      // Log messagesByType breakdown if provided by backend
      if (data.messagesByType) {
        console.log(
          "[Communication] Backend provided messagesByType breakdown:",
          data.messagesByType
        );
        if (
          data.messagesByType.landlord === 0 &&
          (data.messagesByType.tenant > 0 || data.messagesByType.ai > 0)
        ) {
          console.warn(
            "⚠️⚠️⚠️ [Communication] Backend reports 0 landlord messages but conversation has other messages!",
            {
              conversationId: selectedChat.id,
              messagesByType: data.messagesByType,
              totalMessages: conversation?.messages?.length || 0,
            }
          );
        }
      }

      if (conversation) {
        const messages = conversation.messages || [];
        const messagesByType = {
          ai: messages.filter((m) => m.fromUserType === "ai").length,
          tenant: messages.filter((m) => m.fromUserType === "tenant").length,
          landlord: messages.filter((m) => m.fromUserType === "landlord")
            .length,
          vendor: messages.filter((m) => m.fromUserType === "vendor").length,
          other: messages.filter(
            (m) =>
              !["ai", "tenant", "landlord", "vendor"].includes(m.fromUserType)
          ).length,
        };

        const vendorParticipants =
          conversation.participants?.filter(
            (p) => p.userType === "vendor" || p.userType === "handyman"
          ) || [];
        console.log("[Communication] Backend conversation response:", {
          conversationId: conversation.id,
          conversationType: conversation.type,
          totalMessages: messages.length,
          messagesByType,
          participants: conversation.participants?.map((p) => ({
            userId: p.userId,
            userType: p.userType,
            userName: p.userName,
          })),
          vendorCount: vendorParticipants.length,
          vendorParticipants: vendorParticipants.map((p) => ({
            userId: p.userId,
            userType: p.userType,
            userName: p.userName,
            userEmail: p.userEmail,
          })),
        });

        // Explicitly log vendor messages if any exist
        if (messagesByType.vendor > 0) {
          const vendorMessages = messages.filter(
            (m) => m.fromUserType === "vendor"
          );
          console.log("🔧 [Communication] VENDOR MESSAGES FOUND:", {
            count: messagesByType.vendor,
            vendorMessages: vendorMessages.map((m) => ({
              id: m.id || m.messageId,
              fromUserType: m.fromUserType,
              fromUserId: m.fromUserId,
              content:
                typeof m.content === "object"
                  ? m.content?.text
                  : m.content || m.message,
              timestamp: m.timestamp || m.createdAt,
            })),
          });
        } else {
          console.log(
            "⚠️ [Communication] NO VENDOR MESSAGES in backend response. Total messages:",
            messages.length
          );
        }
      }

      return conversation;
    },
    enabled: !!isBackendConversation && !!selectedChat?.id && !!user?.uid,
    refetchInterval: 10000, // Refetch every 10 seconds to catch participant status changes
  });

  const {
    data: messages = [],
    isLoading: isLoadingMessages,
    refetch: refetchMessages,
  } = useQuery({
    queryKey: isPublicLeoSession
      ? ["publicLeoSession", selectedChat?.publicLeoSessionId, user?.uid]
      : isBackendConversation
      ? ["backendConversation", selectedChat?.id, user?.uid]
      : chatKeys.messages(selectedChat?.id),
    queryFn: async () => {
      console.log("[Communication] Messages queryFn called:", {
        isBackendConversation,
        isPublicLeoSession,
        selectedChatId: selectedChat?.id,
        hasBackendConversationData: !!backendConversationData,
        backendMessageCount: backendConversationData?.messages?.length || 0,
      });

      if (isPublicLeoSession && selectedChat?.publicLeoSessionId) {
        const API_URL =
          process.env.REACT_APP_API_URL || "http://localhost:4000/api";
        const token = await getAuth().currentUser?.getIdToken();
        if (!token) {
          throw new Error("Not authenticated");
        }
        const res = await fetch(
          `${API_URL}/landlord/public-leo-sessions/${encodeURIComponent(
            selectedChat.publicLeoSessionId
          )}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(
            err.error ||
              `Failed to load magic-link conversation (${res.status})`
          );
        }
        const data = await res.json();
        const session = data.session || {};
        const history = Array.isArray(session.chatHistory)
          ? session.chatHistory
          : [];
        return history.map((h, i) => {
          const from = h.from;
          const sender =
            from === "user"
              ? "tenant"
              : from === "landlord"
              ? "landlord"
              : "ai";
          const fromUserType = sender;
          return {
            id: `plmsg-${session.id || "s"}-${i}`,
            sender,
            fromUserType,
            content: h.text != null ? String(h.text) : "",
            timestamp: h.timestamp ? new Date(h.timestamp) : new Date(0),
            images: Array.isArray(h.images) ? h.images : [],
          };
        });
      }

      if (isBackendConversation) {
        // Use the conversation data we already fetched
        if (backendConversationData && backendConversationData.messages) {
          const rawMessages = backendConversationData.messages;

          console.log(
            "[Communication] Using backend conversation data for messages:",
            {
              conversationId: backendConversationData.id,
              totalMessages: rawMessages.length,
              vendorMessages: rawMessages.filter(
                (m) => m.fromUserType === "vendor"
              ).length,
              aiMessages: rawMessages.filter((m) => m.fromUserType === "ai")
                .length,
            }
          );

          // Log message breakdown BEFORE transformation
          const messageTypeCounts = {
            ai: rawMessages.filter((m) => m.fromUserType === "ai").length,
            tenant: rawMessages.filter((m) => m.fromUserType === "tenant")
              .length,
            landlord: rawMessages.filter((m) => m.fromUserType === "landlord")
              .length,
            vendor: rawMessages.filter((m) => m.fromUserType === "vendor")
              .length,
            other: rawMessages.filter(
              (m) =>
                !["ai", "tenant", "landlord", "vendor"].includes(m.fromUserType)
            ).length,
          };

          console.log(
            "[Communication] Backend messages received - BREAKDOWN:",
            {
              total: rawMessages.length,
              byType: messageTypeCounts,
              vendorMessages: rawMessages
                .filter((m) => m.fromUserType === "vendor")
                .map((m) => ({
                  id: m.id || m.messageId,
                  fromUserType: m.fromUserType,
                  fromUserId: m.fromUserId,
                  content:
                    typeof m.content === "object"
                      ? m.content?.text
                      : m.content || m.message,
                })),
            }
          );

          // Convert backend message format to match frontend format
          const transformedMessages = rawMessages.map((msg) => {
            // Handle different possible image field names and nested structures
            let messageImages = [];
            if (msg.images && Array.isArray(msg.images)) {
              messageImages = msg.images;
            } else if (
              msg.content?.images &&
              Array.isArray(msg.content.images)
            ) {
              messageImages = msg.content.images;
            } else if (
              msg.message?.images &&
              Array.isArray(msg.message.images)
            ) {
              messageImages = msg.message.images;
            } else if (msg.imageUrls && Array.isArray(msg.imageUrls)) {
              messageImages = msg.imageUrls;
            } else if (msg.attachments && Array.isArray(msg.attachments)) {
              messageImages = msg.attachments;
            }

            return {
              id: msg.id || msg.messageId,
              sender: msg.fromUserType || msg.sender || "tenant",
              fromUserType: msg.fromUserType, // Preserve original for debugging
              fromUserId: msg.fromUserId, // Preserve original for debugging
              content:
                typeof msg.content === "object"
                  ? msg.content.text || msg.content
                  : msg.content || msg.message,
              timestamp: msg.timestamp
                ? new Date(msg.timestamp)
                : new Date(msg.createdAt || Date.now()),
              images: messageImages,
              metadata: msg.metadata || {},
              repairTicketId:
                msg.repairTicketId || msg.metadata?.repairTicketId,
            };
          });

          // Log message breakdown AFTER transformation
          const transformedTypeCounts = {
            ai: transformedMessages.filter((m) => m.sender === "ai").length,
            tenant: transformedMessages.filter((m) => m.sender === "tenant")
              .length,
            landlord: transformedMessages.filter((m) => m.sender === "landlord")
              .length,
            vendor: transformedMessages.filter((m) => m.sender === "vendor")
              .length,
            other: transformedMessages.filter(
              (m) => !["ai", "tenant", "landlord", "vendor"].includes(m.sender)
            ).length,
          };

          console.log("[Communication] Transformed messages - BREAKDOWN:", {
            total: transformedMessages.length,
            byType: transformedTypeCounts,
            landlordMessages: transformedMessages
              .filter((m) => m.sender === "landlord")
              .map((m) => ({
                id: m.id,
                sender: m.sender,
                fromUserType: m.fromUserType,
                fromUserId: m.fromUserId,
                content: m.content?.substring(0, 50),
              })),
            vendorMessages: transformedMessages
              .filter((m) => m.sender === "vendor")
              .map((m) => ({
                id: m.id,
                sender: m.sender,
                fromUserType: m.fromUserType,
                fromUserId: m.fromUserId,
                content: m.content,
              })),
          });

          // CRITICAL: Warn if landlord messages are missing
          if (
            transformedTypeCounts.landlord === 0 &&
            (transformedTypeCounts.tenant > 0 || transformedTypeCounts.ai > 0)
          ) {
            console.warn(
              "⚠️⚠️⚠️ [Communication] NO LANDLORD MESSAGES FOUND in transformed messages!",
              {
                conversationId: selectedChat?.id,
                totalMessages: transformedMessages.length,
                messageBreakdown: transformedTypeCounts,
                rawMessageCount: rawMessages.length,
                rawMessageBreakdown: messageTypeCounts,
                warning:
                  "Landlord messages may be filtered out or not saved correctly",
              }
            );
          }

          // Warn if no vendor messages found but tenant can see them
          if (transformedTypeCounts.vendor === 0 && isBackendConversation) {
            console.warn(
              "[Communication] ⚠️ NO VENDOR MESSAGES in transformed messages, but tenant can see them. Backend may be filtering vendor messages for landlord view."
            );
          }

          return transformedMessages;
        }
        console.warn(
          "[Communication] ⚠️ isBackendConversation is true but backendConversationData.messages is missing!",
          {
            hasBackendConversationData: !!backendConversationData,
            backendConversationDataKeys: backendConversationData
              ? Object.keys(backendConversationData)
              : [],
          }
        );
        return [];
      } else {
        // Load messages from Firestore
        console.log(
          "[Communication] Loading messages from Firestore (not backend):",
          {
            chatId: selectedChat.id,
            reason: "isBackendConversation is false",
            _fromBackend: selectedChat._fromBackend,
          }
        );
        return chatModel.getMessages(selectedChat.id);
      }
    },
    enabled: !!(
      selectedChat?.id &&
      (isPublicLeoSession
        ? true
        : !isBackendConversation || backendConversationData)
    ),
    refetchInterval: 5000, // Refetch every 5 seconds to catch new messages
  });

  const isMessagesPanelLoading =
    isLoadingMessages ||
    (isBackendConversation &&
      isLoadingBackendConversation &&
      !!selectedChat?.id &&
      !backendConversationData);

  // Load handymen if this is a maintenance group chat
  const { data: allHandymen = [] } = useQuery({
    queryKey: ["handymen", user?.uid],
    queryFn: () => HandymanModel.getByOwner(user.uid),
    enabled: !!user?.uid,
  });

  // Filter handymen that are part of this chat
  const chatHandymen = useMemo(() => {
    const handymanIds = selectedChat?.handymanIds || [];
    if (!handymanIds.length || !allHandymen.length) return [];
    return allHandymen.filter((h) => handymanIds.includes(h.id));
  }, [selectedChat?.handymanIds, allHandymen]);

  // Get participants from backend conversation or from selectedChat
  const conversationParticipants = useMemo(() => {
    let participants = [];

    if (isBackendConversation && backendConversationData) {
      participants = backendConversationData.participants || [];
      console.log(
        "[Communication] Using backend conversation participants (before filtering):",
        {
          total: participants.length,
          participants: participants.map((p) => ({
            userId: p.userId,
            userType: p.userType,
            userName: p.userName,
            enabled: p.enabled,
          })),
          vendors: participants.filter(
            (p) => p.userType === "vendor" || p.userType === "handyman"
          ).length,
        }
      );
    } else {
      // For Firestore chats, construct participants from available data
      if (selectedChat?.tenantId && selectedChat?.tenantName) {
        participants.push({
          userId: selectedChat.tenantId,
          userType: "tenant",
          userName: selectedChat.tenantName,
        });
      }
      if (user?.uid) {
        participants.push({
          userId: user.uid,
          userType: "landlord",
          userName: user.displayName || "Landlord",
        });
      }
      // Add vendors from handymen
      chatHandymen.forEach((handyman) => {
        participants.push({
          userId: handyman.id,
          userType: "vendor",
          userName: handyman.name,
        });
      });
      console.log("[Communication] Using Firestore participants (fallback):", {
        total: participants.length,
        participants: participants.map((p) => ({
          userId: p.userId,
          userType: p.userType,
          userName: p.userName,
        })),
      });
    }

    // Note: We don't filter out disabled participants here - the landlord should see all participants
    // Disabled status is handled in the UI rendering (showing badge/toggle)
    // This ensures the landlord can see and manage all participants, including disabled ones

    // Log detailed participant information for debugging
    const vendorParticipants = participants.filter(
      (p) => p.userType === "vendor" || p.userType === "handyman"
    );
    console.log(
      "[Communication] Final participants (all, including disabled):",
      {
        total: participants.length,
        vendorCount: vendorParticipants.length,
        participants: participants.map((p) => ({
          userId: p.userId,
          userType: p.userType,
          userName: p.userName,
          userEmail: p.userEmail,
          enabled: p.enabled,
        })),
        vendors: vendorParticipants.map((p) => ({
          userId: p.userId,
          userType: p.userType,
          userName: p.userName,
          userEmail: p.userEmail,
          enabled: p.enabled,
        })),
      }
    );

    if (vendorParticipants.length === 0 && isBackendConversation) {
      console.warn(
        "[Communication] ⚠️ NO VENDOR PARTICIPANTS FOUND in backend response, but tenant can see them. Backend may be filtering vendors for landlord view."
      );
    }

    return participants;
  }, [
    isBackendConversation,
    backendConversationData,
    selectedChat,
    user,
    chatHandymen,
  ]);

  // Get disabled participants from backend conversation
  const disabledParticipants = useMemo(() => {
    if (isBackendConversation && backendConversationData) {
      return backendConversationData.disabledParticipants || [];
    }
    return [];
  }, [isBackendConversation, backendConversationData]);

  // Check if current user is a landlord in this conversation
  const isLandlordInConversation = useMemo(() => {
    return conversationParticipants.some(
      (p) => p.userId === user?.uid && p.userType === "landlord"
    );
  }, [conversationParticipants, user?.uid]);

  // Function to toggle participant status
  const handleToggleParticipant = useCallback(
    async (participantUserId, enabled) => {
      if (!selectedChat?.id || !user?.uid) return;

      try {
        const API_URL =
          process.env.REACT_APP_API_URL || "http://localhost:4000/api";
        if (!API_URL) {
          throw new Error("REACT_APP_API_URL not configured");
        }

        const response = await fetch(
          `${API_URL}/conversations/${selectedChat.id}/participant-status`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              participantUserId,
              enabled,
              requestingUserId: user.uid,
            }),
          }
        );

        if (!response.ok) {
          const error = await response
            .json()
            .catch(() => ({ error: "Unknown error" }));
          throw new Error(
            error.details ||
              error.error ||
              `Failed to ${enabled ? "enable" : "disable"} participant`
          );
        }

        const result = await response.json();
        message.success(
          result.message ||
            `Participant ${enabled ? "enabled" : "disabled"} successfully`
        );

        // Refresh conversation data
        await refetchConversation();
        await refetchMessages();

        // Also refresh the chats list
        queryClient.invalidateQueries({ queryKey: ["chats", user?.uid] });
      } catch (error) {
        console.error("Error toggling participant:", error);
        message.error(
          error.message ||
            `Failed to ${enabled ? "enable" : "disable"} participant`
        );
      }
    },
    [
      selectedChat?.id,
      user?.uid,
      refetchConversation,
      refetchMessages,
      queryClient,
    ]
  );

  const tenants = useMemo(() => {
    if (Array.isArray(tenantsData)) {
      return tenantsData;
    } else if (tenantsData?.tenants && Array.isArray(tenantsData.tenants)) {
      return tenantsData.tenants;
    } else {
      return [];
    }
  }, [tenantsData]);

  // Debug: Log tenant and unit data when they load
  useEffect(() => {
    if (tenants.length > 0 && units.length > 0) {
      console.log("Communication: Loaded data", {
        tenants: tenants.map((t) => ({
          id: t.id,
          name: t.firstName + " " + t.lastName,
          unitId: t.unitId,
          propertyId: t.propertyId,
          unitNumber: t.unitNumber,
          ownerId: t.ownerId,
        })),
        units: units.map((u) => ({
          id: u.id,
          ownerId: u.ownerId,
          unitNumber: u.unitNumber,
        })),
      });
    } else {
      console.log("Communication: Data not ready", {
        tenantsData: tenantsData,
        tenantsDataType: typeof tenantsData,
        tenantsDataIsArray: Array.isArray(tenantsData),
        tenantsLength: tenants.length,
        unitsLength: units.length,
      });
    }
  }, [tenants, units, tenantsData]);

  // Request notification permission
  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      console.log("This browser does not support notifications");
      return;
    }

    if (Notification.permission === "granted") {
      setNotificationPermission("granted");
      return;
    }

    if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
    } else {
      setNotificationPermission("denied");
    }
  };

  // Show notification for new messages
  const showNotification = useCallback(
    (message, senderName) => {
      if (notificationPermission !== "granted") return;

      const notification = new Notification(`New Message from ${senderName}`, {
        body:
          message.content.length > 100
            ? message.content.substring(0, 100) + "..."
            : message.content,
        icon: "/logo192.png",
        badge: "/logo192.png",
        tag: "communication-message",
        requireInteraction: false,
        silent: false,
      });

      // Auto-close notification after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    },
    [notificationPermission]
  );

  // Create chats for each tenant and load existing chats
  useEffect(() => {
    if (!user?.uid || !units.length) {
      setIsInitializingChats(false);
      return;
    }

    // Only initialize chats once, not on every tenantId change
    if (chatsInitializedRef.current) {
      return;
    }

    // Store the initial tenantId from URL for prioritized loading
    if (tenantId && !initialTenantIdRef.current) {
      initialTenantIdRef.current = tenantId;
    }

    const initializeTenantChats = async () => {
      try {
        setIsInitializingChats(true);

        const tenantChats = [];

        // First, load all existing chats (including maintenance group chats) from Firestore
        // Also try to load from backend API if available
        let allChats = [];
        let backendConversations = [];
        let combinedChats = []; // Store backend chats merged with Firestore chats (has _fromBackend: true)

        try {
          allChats = await chatModel.getLandlordChats(user.uid);

          // Normalize all Date objects to strings to prevent React rendering errors
          // Also check localStorage for backend conversationIds and mark chats accordingly
          allChats = allChats.map((chat) => {
            const normalized = { ...chat };

            // CRITICAL: Check if this tenant has a backend conversationId in localStorage
            // If so, this chat should use the backend conversation, not Firestore
            if (normalized.tenantId) {
              const localStorageKey = chatModel.CONV_KEY(normalized.tenantId);
              const backendConversationId =
                typeof window !== "undefined"
                  ? localStorage.getItem(localStorageKey)
                  : null;

              if (backendConversationId) {
                // This tenant has a backend conversation - use that ID and mark as backend
                console.log(
                  `[Communication] Found backend conversationId for tenant ${normalized.tenantId}: ${backendConversationId} (updating Firestore chat)`
                );
                normalized.id = backendConversationId;
                normalized._fromBackend = true;
              }
            }

            // Convert createdAt if it's a Date object
            if (normalized.createdAt instanceof Date) {
              normalized.createdAt = normalized.createdAt.toISOString();
            } else if (
              normalized.createdAt &&
              typeof normalized.createdAt === "object" &&
              normalized.createdAt.toDate
            ) {
              // Handle Firestore Timestamp
              normalized.createdAt = normalized.createdAt
                .toDate()
                .toISOString();
            }
            // Convert updatedAt if it's a Date object
            if (normalized.updatedAt instanceof Date) {
              normalized.updatedAt = normalized.updatedAt.toISOString();
            } else if (
              normalized.updatedAt &&
              typeof normalized.updatedAt === "object" &&
              normalized.updatedAt.toDate
            ) {
              // Handle Firestore Timestamp
              normalized.updatedAt = normalized.updatedAt
                .toDate()
                .toISOString();
            }
            // Convert lastMessageTime if it's a Date object
            if (normalized.lastMessageTime instanceof Date) {
              normalized.lastMessageTime = formatTime(
                normalized.lastMessageTime
              );
            } else if (
              normalized.lastMessageTime &&
              typeof normalized.lastMessageTime === "object" &&
              normalized.lastMessageTime.toDate
            ) {
              // Handle Firestore Timestamp
              normalized.lastMessageTime = formatTime(
                normalized.lastMessageTime.toDate()
              );
            }
            return normalized;
          });

          // Also try to load conversations from backend API
          // Following the guide: GET /api/conversations?userId={landlordId}&userType=landlord
          try {
            const API_URL =
              process.env.REACT_APP_API_URL || "http://localhost:4000/api";
            if (API_URL) {
              // Fetch all conversations (including maintenance_group)
              // The backend returns all conversations, we'll filter for maintenance chats
              const apiUrl = `${API_URL}/conversations?userId=${user.uid}&userType=landlord&limit=100`;

              console.log(
                `[Communication] Fetching conversations from: ${apiUrl}`
              );

              const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                },
              });

              console.log(
                `[Communication] API response status: ${response.status} ${response.statusText}`
              );

              if (response.ok) {
                const data = await response.json();
                console.log(`[Communication] API response data:`, data);

                if (data.success && Array.isArray(data.conversations)) {
                  backendConversations = data.conversations;
                  console.log(
                    `[Communication] Loaded ${backendConversations.length} conversations from backend API`
                  );

                  // Log all conversation types
                  const conversationTypes = [
                    ...new Set(
                      backendConversations.map((c) => c.conversationType)
                    ),
                  ];
                  console.log(
                    `[Communication] Conversation types in response:`,
                    conversationTypes
                  );

                  // Log all conversations (all are now regular tenant-landlord chats)
                  console.log(
                    `[Communication] Loaded ${backendConversations.length} conversations from backend:`,
                    backendConversations.map((c) => ({
                      id: c.id,
                      conversationType: c.conversationType,
                      ticketId: c.context?.relatedTicketId,
                      hasVendor: c.participants?.some(
                        (p) =>
                          p.userType === "vendor" || p.userType === "handyman"
                      ),
                      participants: c.participants?.map((p) => ({
                        userId: p.userId,
                        userType: p.userType,
                      })),
                      messageCount: c.messages?.length || 0,
                      messagesByType: c.messages
                        ? {
                            ai: c.messages.filter(
                              (m) => m.fromUserType === "ai"
                            ).length,
                            tenant: c.messages.filter(
                              (m) => m.fromUserType === "tenant"
                            ).length,
                            landlord: c.messages.filter(
                              (m) => m.fromUserType === "landlord"
                            ).length,
                            vendor: c.messages.filter(
                              (m) => m.fromUserType === "vendor"
                            ).length,
                            other: c.messages.filter(
                              (m) =>
                                ![
                                  "ai",
                                  "tenant",
                                  "landlord",
                                  "vendor",
                                ].includes(m.fromUserType)
                            ).length,
                          }
                        : null,
                    }))
                  );

                  // Explicitly check for vendors in each conversation
                  backendConversations.forEach((conv) => {
                    const vendors =
                      conv.participants?.filter(
                        (p) =>
                          p.userType === "vendor" || p.userType === "handyman"
                      ) || [];
                    if (vendors.length > 0) {
                      console.log(
                        `🔧 [Communication] Conversation ${conv.id} has ${vendors.length} vendor(s) in participants:`,
                        vendors.map((v) => ({
                          userId: v.userId,
                          userType: v.userType,
                          userName: v.userName,
                          userEmail: v.userEmail,
                        }))
                      );
                    } else {
                      console.log(
                        `[Communication] Conversation ${conv.id} has NO vendors in participants. Total participants:`,
                        conv.participants?.length || 0
                      );
                    }

                    if (conv.messages && conv.messages.length > 0) {
                      const vendorMessages = conv.messages.filter(
                        (m) => m.fromUserType === "vendor"
                      );
                      if (vendorMessages.length > 0) {
                        console.log(
                          `🔧 [Communication] Conversation ${conv.id} has ${vendorMessages.length} vendor messages:`,
                          vendorMessages.map((m) => ({
                            id: m.id || m.messageId,
                            fromUserType: m.fromUserType,
                            fromUserId: m.fromUserId,
                            content:
                              typeof m.content === "object"
                                ? m.content?.text
                                : m.content || m.message,
                          }))
                        );
                      }
                    }
                  });
                } else {
                  console.warn(
                    "[Communication] Backend API response missing success or conversations:",
                    data
                  );
                  console.warn("[Communication] Response structure:", {
                    hasSuccess: "success" in data,
                    successValue: data.success,
                    hasConversations: "conversations" in data,
                    conversationsType: Array.isArray(data.conversations),
                    conversationsLength: data.conversations?.length,
                    fullData: data,
                  });
                }
              } else {
                const errorText = await response.text();
                console.error(
                  "[Communication] Backend API error response:",
                  response.status,
                  errorText
                );
              }
            } else {
              console.warn("[Communication] REACT_APP_API_URL not configured");
            }
          } catch (e) {
            console.error(
              "[Communication] Could not load conversations from backend API:",
              e
            );
            console.error("[Communication] Error details:", {
              message: e.message,
              stack: e.stack,
              name: e.name,
            });
          }

          // Vendors with ticket access are typically notified by email (property setting); legacy chats may still list vendor participants
          // No separate maintenance chats are created

          // Merge backend conversations with Firestore chats
          // Convert backend conversations to match Firestore chat format
          // Fetch ticket details for all backend conversations that have ticketIds
          const ticketDetailsMap = new Map();
          const conversationsWithTickets = backendConversations.filter(
            (conv) => conv.context?.relatedTicketId
          );

          if (conversationsWithTickets.length > 0) {
            try {
              const repairModelModule = await import(
                "../lib/firebase/repairModel"
              );
              const repairModel =
                repairModelModule.default || repairModelModule;

              // Fetch all ticket details in parallel
              const ticketPromises = conversationsWithTickets.map(
                async (conv) => {
                  const ticketId = conv.context?.relatedTicketId;
                  if (!ticketId) return null;

                  try {
                    const ticket = await repairModel.getById(ticketId);
                    return { ticketId, ticket };
                  } catch (error) {
                    console.warn(
                      `[Communication] Could not fetch ticket ${ticketId}:`,
                      error
                    );
                    return null;
                  }
                }
              );

              const ticketResults = await Promise.all(ticketPromises);
              ticketResults.forEach((result) => {
                if (result && result.ticket) {
                  ticketDetailsMap.set(result.ticketId, result.ticket);
                }
              });
            } catch (error) {
              console.error(
                "[Communication] Error fetching ticket details:",
                error
              );
            }
          }

          // Transform backend conversations to frontend format
          // Following the guide: https://github.com/your-repo/docs/maintenance-chat-integration.md
          // Use Promise.all since we're now making async calls to fetch last messages
          const backendChats = await Promise.all(
            backendConversations.map(async (conv) => {
              // All conversations are now regular tenant-landlord chats
              // Maintenance may still show vendor participants from older threads; new tickets use vendor email, not chat add
              // Extract ticketId from context (if present, for reference)
              const ticketId = conv.context?.relatedTicketId || null;

              // Get ticket details if available
              const ticketDetails = ticketId
                ? ticketDetailsMap.get(ticketId)
                : null;

              // Extract vendor/handyman IDs from participants
              const handymanIds = conv.participants
                ? conv.participants
                    .filter(
                      (p) =>
                        p.userType === "vendor" || p.userType === "handyman"
                    )
                    .map((p) => p.userId)
                : [];

              // Extract tenant info
              const tenantParticipant = conv.participants?.find(
                (p) => p.userType === "tenant"
              );

              // Check if backend provides lastMessage directly on the conversation object
              // Also check metadata.lastMessage as a fallback
              let lastMessageText =
                conv.lastMessage || conv.metadata?.lastMessage || "";
              let lastMessageObj = null;

              // Get last message from messages array (if available in the response)
              // Only use messages array if we don't already have lastMessage from conversation object
              if (
                !lastMessageText &&
                conv.messages &&
                conv.messages.length > 0
              ) {
                lastMessageObj = conv.messages[conv.messages.length - 1];

                // Extract last message text - handle multiple content formats
                if (lastMessageObj) {
                  if (
                    typeof lastMessageObj.content === "object" &&
                    lastMessageObj.content?.text
                  ) {
                    lastMessageText = lastMessageObj.content.text;
                  } else if (typeof lastMessageObj.content === "string") {
                    lastMessageText = lastMessageObj.content;
                  } else if (lastMessageObj.message) {
                    lastMessageText = lastMessageObj.message;
                  } else if (
                    typeof lastMessageObj.content === "object" &&
                    lastMessageObj.content
                  ) {
                    // Try to extract text from nested content object
                    lastMessageText =
                      lastMessageObj.content.text ||
                      lastMessageObj.content.message ||
                      "";
                  }
                }
              }

              // Log for debugging
              if (
                conv.messages &&
                conv.messages.length > 0 &&
                !lastMessageText
              ) {
                console.warn(
                  `[Communication] ⚠️ Conversation ${conv.id} has ${conv.messages.length} messages but couldn't extract text. Message structure:`,
                  {
                    hasContent: !!lastMessageObj?.content,
                    contentType: typeof lastMessageObj?.content,
                    hasMessage: !!lastMessageObj?.message,
                    messageKeys: lastMessageObj
                      ? Object.keys(lastMessageObj)
                      : [],
                  }
                );
              }

              // If no last message in the response, try to fetch it from the backend API
              if (!lastMessageText && conv.id && user?.uid) {
                try {
                  const apiUrl =
                    process.env.REACT_APP_API_URL ||
                    "http://localhost:4000/api";
                  if (apiUrl) {
                    // Include userId parameter for proper authentication/authorization
                    const fetchUrl = `${apiUrl}/conversations/${conv.id}?userId=${user.uid}&limit=1000&includeAllMessages=true`;
                    console.log(
                      `[Communication] Fetching last message for conversation ${conv.id} from: ${fetchUrl}`
                    );

                    const response = await fetch(fetchUrl, {
                      method: "GET",
                      headers: { "Content-Type": "application/json" },
                    });

                    if (response.ok) {
                      const data = await response.json();
                      const fullConversation = data.success
                        ? data.conversation
                        : null;

                      if (
                        fullConversation?.messages &&
                        fullConversation.messages.length > 0
                      ) {
                        lastMessageObj =
                          fullConversation.messages[
                            fullConversation.messages.length - 1
                          ];

                        // Handle different message content formats
                        if (
                          typeof lastMessageObj.content === "object" &&
                          lastMessageObj.content?.text
                        ) {
                          lastMessageText = lastMessageObj.content.text;
                        } else if (typeof lastMessageObj.content === "string") {
                          lastMessageText = lastMessageObj.content;
                        } else if (lastMessageObj.message) {
                          lastMessageText = lastMessageObj.message;
                        } else if (
                          typeof lastMessageObj.content === "object" &&
                          lastMessageObj.content
                        ) {
                          // Try to stringify if it's an object
                          lastMessageText = JSON.stringify(
                            lastMessageObj.content
                          );
                        }

                        console.log(
                          `[Communication] ✅ Fetched last message for conversation ${conv.id}:`,
                          lastMessageText.substring(0, 50)
                        );
                      } else {
                        console.log(
                          `[Communication] ⚠️ Conversation ${conv.id} has no messages in full fetch`
                        );
                      }
                    } else {
                      console.warn(
                        `[Communication] ⚠️ Failed to fetch conversation ${conv.id}: ${response.status} ${response.statusText}`
                      );
                    }
                  }
                } catch (error) {
                  console.warn(
                    `[Communication] ⚠️ Could not fetch last message for conversation ${conv.id}:`,
                    error
                  );
                  // Continue with empty string - will show "No messages yet"
                }
              }

              // If still no message text, set default
              if (!lastMessageText) {
                lastMessageText = "No messages yet";
                console.log(
                  `[Communication] ⚠️ Conversation ${conv.id} (${
                    conv.context?.chatName ||
                    tenantParticipant?.userName ||
                    "Unknown"
                  }) has no last message text after all attempts`
                );
              } else {
                console.log(
                  `[Communication] ✅ Conversation ${conv.id} (${
                    conv.context?.chatName ||
                    tenantParticipant?.userName ||
                    "Unknown"
                  }) has last message: "${lastMessageText.substring(0, 50)}${
                    lastMessageText.length > 50 ? "..." : ""
                  }"`
                );
              }

              // Convert timestamps - format lastMessageTime as string for display
              const lastMessageTimeRaw =
                lastMessageObj?.timestamp || lastMessageObj?.createdAt
                  ? new Date(
                      lastMessageObj.timestamp || lastMessageObj.createdAt
                    )
                  : conv.metadata?.lastMessageAt
                  ? new Date(conv.metadata.lastMessageAt)
                  : conv.metadata?.updatedAt
                  ? new Date(conv.metadata.updatedAt)
                  : new Date();
              // Format as string to avoid React rendering Date objects directly
              let lastMessageTime = "Just now";
              try {
                const now = new Date();
                const diffInHours =
                  (now - lastMessageTimeRaw) / (1000 * 60 * 60);
                if (diffInHours < 1) {
                  lastMessageTime = "Just now";
                } else if (diffInHours < 24) {
                  lastMessageTime = `${Math.floor(diffInHours)} ${
                    Math.floor(diffInHours) === 1 ? "hour" : "hours"
                  } ago`;
                } else {
                  lastMessageTime = lastMessageTimeRaw.toLocaleDateString();
                }
              } catch (error) {
                console.error("Error formatting lastMessageTime:", error);
                lastMessageTime = "Just now";
              }
              // Don't store Date objects - convert to ISO strings to avoid React rendering issues
              const createdAt = conv.metadata?.createdAt
                ? typeof conv.metadata.createdAt === "string"
                  ? conv.metadata.createdAt
                  : new Date(conv.metadata.createdAt).toISOString()
                : new Date().toISOString();
              const updatedAt = conv.metadata?.updatedAt
                ? typeof conv.metadata.updatedAt === "string"
                  ? conv.metadata.updatedAt
                  : new Date(conv.metadata.updatedAt).toISOString()
                : new Date().toISOString();

              // Extract unitId from conversation context or ticket details
              // Note: tenants and units are from the component scope, accessible via closure
              const unitId =
                conv.context?.unitId || ticketDetails?.unitId || null;

              // If we have tenantId, try to find the unitId from the tenant data
              const tenantIdForLookup =
                tenantParticipant?.userId || ticketDetails?.tenantId || null;
              let resolvedUnitId = unitId;
              if (
                !resolvedUnitId &&
                tenantIdForLookup &&
                tenants &&
                tenants.length > 0
              ) {
                // Try to find unitId from the tenants array (from component scope)
                const tenant = tenants.find((t) => t.id === tenantIdForLookup);
                if (tenant?.unitId) {
                  resolvedUnitId = tenant.unitId;
                  console.log(
                    `[Communication] Resolved unitId ${resolvedUnitId} from tenant ${tenantIdForLookup} for conversation ${conv.id}`
                  );
                }
              }

              // Get unitNumber from ticket, context, or by looking up the unit
              let unitNumber =
                ticketDetails?.unitNumber || conv.context?.unitNumber || null;
              if (!unitNumber && resolvedUnitId && units && units.length > 0) {
                // units is from component scope, accessible via closure
                const unit = units.find((u) => u.id === resolvedUnitId);
                if (unit) {
                  unitNumber = unit.unitNumber || null;
                  console.log(
                    `[Communication] Resolved unitNumber ${unitNumber} from unit ${resolvedUnitId} for conversation ${conv.id}`
                  );
                }
              }

              return {
                id: conv.id,
                type: undefined, // No special types - all are regular tenant-landlord chats
                ticketId: ticketId,
                ticketTitle:
                  ticketDetails?.title ||
                  conv.context?.conversationTopic ||
                  null,
                unitId: resolvedUnitId, // Add unitId to the chat object
                unitNumber: unitNumber,
                handymanIds: handymanIds,
                landlordId: user.uid,
                tenantId:
                  tenantParticipant?.userId || ticketDetails?.tenantId || null,
                tenantName: tenantParticipant?.userName || "Tenant",
                chatName:
                  conv.context?.chatName ||
                  conv.context?.conversationTopic ||
                  tenantParticipant?.userName ||
                  "Chat",
                status:
                  conv.metadata?.isActive !== false ? "active" : "archived",
                lastMessage: lastMessageText,
                lastMessageTime: lastMessageTime, // Now a formatted string, not a Date object
                unreadCount: 0, // TODO: Calculate from unread messages
                createdAt: createdAt, // ISO string, not Date object
                updatedAt: updatedAt, // ISO string, not Date object
                leoEnabled: conv.leoEnabled !== false, // Defaults to true if undefined
                participants: conv.participants || [],
                // Flag to indicate this came from backend API
                _fromBackend: true,
              };
            })
          );

          console.log(
            `[Communication] Transformed ${backendChats.length} backend conversations to chats`
          );

          // Combine Firestore chats and backend conversations
          // Deduplicate by ID - backend conversations take precedence (they're more up-to-date)
          const chatMap = new Map();

          // First, add all Firestore chats
          allChats.forEach((chat) => {
            if (chat.id) {
              chatMap.set(chat.id, chat);
            }
          });

          // Then, add backend conversations (they will overwrite Firestore chats with same ID)
          backendChats.forEach((chat) => {
            if (chat.id) {
              chatMap.set(chat.id, chat);
            }
          });

          combinedChats = Array.from(chatMap.values());

          // All chats are now regular tenant-landlord chats (vendors are added as participants)
          // No separate maintenance chat processing needed

          console.log(
            `[Communication] Combined ${
              combinedChats.length
            } chats (Firestore + Backend), ${
              combinedChats.filter((c) => c._fromBackend).length
            } from backend`
          );
        } catch (error) {
          console.error("Error loading existing chats:", error);
        }

        // Helper function to load a single tenant's chat
        const loadTenantChat = async (tenant) => {
          try {
            // CRITICAL FIX: Check localStorage for a backend conversation ID first
            // This ensures landlord uses the same conversation ID as the tenant
            const localStorageKey = chatModel.CONV_KEY(tenant.id);
            const cachedConversationId =
              typeof window !== "undefined"
                ? localStorage.getItem(localStorageKey)
                : null;

            let chatId;
            let isBackendConversation = false;

            if (cachedConversationId) {
              console.log(
                `[Communication] ✅ Found cached backend conversation ID for tenant ${tenant.id}: ${cachedConversationId}`
              );
              chatId = cachedConversationId;
              isBackendConversation = true;
            } else {
              // No cache found - call backend API to get/create conversation (same as tenant does)
              // This ensures landlord and tenant use the same conversationId
              console.log(
                `[Communication] ⚠️ No cached backend conversation ID for tenant ${tenant.id}, calling backend API`
              );
              try {
                const apiUrl =
                  process.env.REACT_APP_API_URL || "http://localhost:4000/api";
                if (apiUrl) {
                  const response = await fetch(
                    `${apiUrl}/conversations/create`,
                    {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        tenantId: tenant.id,
                        landlordId: user.uid,
                        unitId: tenant.unitId || null,
                        propertyId: tenant.propertyId || null,
                      }),
                    }
                  );

                  if (response.ok) {
                    const data = await response.json();
                    if (data.conversationId) {
                      // Save to localStorage for future use
                      localStorage.setItem(
                        localStorageKey,
                        data.conversationId
                      );
                      chatId = data.conversationId;
                      isBackendConversation = true;
                      console.log(
                        `[Communication] ✅ Created/retrieved backend conversation for tenant ${tenant.id}: ${chatId}`
                      );
                    } else {
                      throw new Error("Backend did not return conversationId");
                    }
                  } else {
                    throw new Error(`Backend API returned ${response.status}`);
                  }
                } else {
                  throw new Error("REACT_APP_API_URL not configured");
                }
              } catch (error) {
                console.error(
                  `[Communication] ⚠️ Failed to get backend conversation for tenant ${tenant.id}, falling back to Firestore:`,
                  error
                );
                // Fallback to Firestore only if backend API fails
                chatId = await chatModel.getOrCreateChat(tenant.id, user.uid, {
                  name: tenant.name,
                  email: tenant.email || "",
                  unitNumber: tenant.unitId
                    ? units.find((u) => u.id === tenant.unitId)?.unitNumber ||
                      "N/A"
                    : "N/A",
                  unitAddress: getUnitAddress(tenant),
                  landlordName: user.displayName || "Landlord",
                });
                console.log(
                  `[Communication] Created/retrieved Firestore chat for tenant ${tenant.id}: ${chatId}`
                );
              }
            }

            // If chat creation failed, create a local chat entry anyway
            if (!chatId) {
              chatId = `chat_${tenant.id}_${user.uid}`;
            }

            // Fetch the actual last message for this chat
            let lastMessage = "No messages yet";
            let lastMessageTime = "Just now";
            let leoEnabled = true; // Default to true if not found

            // For backend conversations, fetch from backend API; for Firestore chats, fetch from Firestore
            if (isBackendConversation && chatId) {
              try {
                const apiUrl =
                  process.env.REACT_APP_API_URL || "http://localhost:4000/api";
                if (apiUrl) {
                  const fetchUrl = `${apiUrl}/conversations/${chatId}?userId=${user.uid}&limit=1000&includeAllMessages=true`;
                  console.log(
                    `[Communication] Fetching last message for backend conversation ${chatId} (tenant ${tenant.name})`
                  );

                  const response = await fetch(fetchUrl, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                  });

                  if (response.ok) {
                    const data = await response.json();
                    const fullConversation = data.success
                      ? data.conversation
                      : null;

                    if (
                      fullConversation?.messages &&
                      fullConversation.messages.length > 0
                    ) {
                      const lastMsg =
                        fullConversation.messages[
                          fullConversation.messages.length - 1
                        ];

                      // Handle different message content formats
                      if (
                        typeof lastMsg.content === "object" &&
                        lastMsg.content?.text
                      ) {
                        lastMessage = lastMsg.content.text;
                      } else if (typeof lastMsg.content === "string") {
                        lastMessage = lastMsg.content;
                      } else if (lastMsg.message) {
                        lastMessage = lastMsg.message;
                      }

                      // Get timestamp
                      const timestamp = lastMsg.timestamp || lastMsg.createdAt;
                      if (timestamp) {
                        lastMessageTime = formatTime(new Date(timestamp));
                      }

                      console.log(
                        `[Communication] ✅ Fetched last message for backend conversation ${chatId} (tenant ${
                          tenant.name
                        }): "${lastMessage.substring(0, 50)}${
                          lastMessage.length > 50 ? "..." : ""
                        }"`
                      );
                    } else {
                      console.log(
                        `[Communication] ⚠️ Backend conversation ${chatId} (tenant ${tenant.name}) has no messages`
                      );
                    }
                  } else {
                    console.warn(
                      `[Communication] ⚠️ Failed to fetch backend conversation ${chatId} (tenant ${tenant.name}): ${response.status}`
                    );
                  }
                }
              } catch (error) {
                console.warn(
                  `[Communication] ⚠️ Could not fetch last message for backend conversation ${chatId} (tenant ${tenant.name}):`,
                  error
                );
              }
            } else {
              // Firestore chat - fetch from Firestore
              try {
                const messages = await chatModel.getMessages(chatId, null, 1); // Get only the last message
                if (messages.length > 0) {
                  const lastMsg = messages[messages.length - 1];
                  lastMessage = lastMsg.content || "No messages yet";
                  lastMessageTime = formatTime(lastMsg.timestamp);
                  console.log(
                    `[Communication] ✅ Fetched last message for Firestore chat ${chatId} (tenant ${
                      tenant.name
                    }): "${lastMessage.substring(0, 50)}${
                      lastMessage.length > 50 ? "..." : ""
                    }"`
                  );
                } else {
                  console.log(
                    `[Communication] ⚠️ Firestore chat ${chatId} (tenant ${tenant.name}) has no messages`
                  );
                }
              } catch (error) {
                console.warn(
                  `[Communication] ⚠️ Could not fetch messages for Firestore chat ${chatId} (tenant ${tenant.name}):`,
                  error
                );
              }
            }

            // Fetch the full chat document to get leoEnabled and other fields
            // Only try Firestore if this is not a backend conversation
            if (!isBackendConversation) {
              try {
                const fullChatData = await chatModel.getChat(chatId);
                if (fullChatData) {
                  // Preserve the actual boolean value, default to true if undefined
                  leoEnabled =
                    fullChatData.leoEnabled !== undefined
                      ? fullChatData.leoEnabled
                      : true;
                }
              } catch (error) {
                console.log(
                  `Could not fetch full chat data for ${chatId}, using defaults`
                );
              }
            }

            // Create chat entry for the tenant
            return {
              id: chatId, // This is already the backend ID if cached, or Firestore ID otherwise
              tenantId: tenant.id,
              tenantName: tenant.name,
              unitNumber: tenant.unitId
                ? units.find((u) => u.id === tenant.unitId)?.unitNumber || "N/A"
                : "N/A",
              unitAddress: getUnitAddress(tenant),
              lastMessage,
              lastMessageTime,
              unreadCount: 0,
              status: "active",
              leoEnabled: leoEnabled,
              _fromBackend: isBackendConversation, // Mark as backend if we're using cached backend conversationId
            };
          } catch (error) {
            console.error(
              `Error creating chat for tenant ${tenant.name}:`,
              error
            );

            // Even if chat creation fails, create a local entry so landlord can still see the tenant
            // Try to fetch last message even for failed chats
            let lastMessage = "Chat not initialized";
            let lastMessageTime = "Just now";
            let leoEnabled = true; // Default to true

            try {
              const messages = await chatModel.getMessages(
                `chat_${tenant.id}_${user.uid}`,
                null,
                1
              );
              if (messages.length > 0) {
                const lastMsg = messages[messages.length - 1];
                lastMessage = lastMsg.content;
                lastMessageTime = formatTime(lastMsg.timestamp);
              }
            } catch (error) {
              console.log(
                `No messages found for failed chat ${tenant.id}, using default`
              );
            }

            // Try to fetch the full chat document even for failed chats
            // Check if backend conversation exists first
            let fallbackChatId = `chat_${tenant.id}_${user.uid}`;
            let isFallbackBackendConversation = false;
            try {
              const cachedConvId = localStorage.getItem(`conv-${tenant.id}`);
              if (cachedConvId) {
                fallbackChatId = cachedConvId;
                isFallbackBackendConversation = true;
                console.log(
                  `[Communication] Found cached backend conversationId for failed chat tenant ${tenant.id}: ${cachedConvId}`
                );
              }
            } catch (error) {
              console.warn(
                `[Communication] Could not check for cached conversationId:`,
                error
              );
            }

            // Only fetch from Firestore if not a backend conversation
            if (!isFallbackBackendConversation) {
              try {
                const fullChatData = await chatModel.getChat(fallbackChatId);
                if (fullChatData) {
                  // Preserve the actual boolean value, default to true if undefined
                  leoEnabled =
                    fullChatData.leoEnabled !== undefined
                      ? fullChatData.leoEnabled
                      : true;
                }
              } catch (error) {
                console.log(
                  `Could not fetch full chat data for failed chat ${tenant.id}, using defaults`
                );
              }
            }

            return {
              id: fallbackChatId, // This is already the backend ID if cached, or Firestore ID otherwise
              tenantId: tenant.id,
              tenantName: tenant.name,
              unitNumber: tenant.unitId
                ? units.find((u) => u.id === tenant.unitId)?.unitNumber || "N/A"
                : "N/A",
              unitAddress: getUnitAddress(tenant),
              lastMessage,
              lastMessageTime,
              unreadCount: 0,
              status: "error",
              leoEnabled: leoEnabled,
              _fromBackend: isFallbackBackendConversation,
            };
          }
        };

        // Prioritize loading the specific tenant chat if initialTenantId is in URL (only on first load)
        const initialTenantId = initialTenantIdRef.current;
        if (initialTenantId) {
          const prioritizedTenant = tenants.find(
            (t) => t.id === initialTenantId
          );
          if (prioritizedTenant) {
            console.log(
              `[Communication] Prioritizing chat load for tenant: ${prioritizedTenant.name}`
            );
            const prioritizedChat = await loadTenantChat(prioritizedTenant);
            tenantChats.push(prioritizedChat);

            // Immediately update state with the prioritized chat so it can be selected
            // This allows messages to start loading before all other chats finish
            setChats([prioritizedChat]);
            setAllChats([prioritizedChat]);
            console.log(
              `[Communication] Updated state with prioritized chat, messages can now load`
            );

            // Pre-load messages for the prioritized chat to make it ready immediately
            try {
              await chatModel.getMessages(prioritizedChat.id, null, 50); // Load first 50 messages
              console.log(
                `[Communication] Pre-loaded messages for prioritized chat: ${prioritizedChat.id}`
              );
            } catch (error) {
              console.warn(
                `[Communication] Could not pre-load messages for prioritized chat:`,
                error
              );
            }
          }
        }

        // Load remaining tenant chats in parallel (excluding the prioritized one if it exists)
        const remainingTenants = initialTenantId
          ? tenants.filter((t) => t.id !== initialTenantId)
          : tenants;

        if (remainingTenants.length > 0) {
          // Load remaining chats in parallel for better performance
          const remainingChatPromises = remainingTenants.map((tenant) =>
            loadTenantChat(tenant)
          );
          const remainingChats = await Promise.all(remainingChatPromises);
          tenantChats.push(...remainingChats);
        }

        // Merge tenantChats (Firestore) with combinedChats (Backend + Firestore)
        // Backend chats take precedence (they have _fromBackend: true and include vendor messages)
        // IMPORTANT: Match by tenantId, not chat ID, since backend and Firestore may have different IDs
        const chatMapForFinal = new Map(); // Key: tenantId, Value: chat object
        const chatIdMap = new Map(); // Key: chatId, Value: chat object (for lookup by ID)

        console.log(
          `[Communication] Merging chats: ${tenantChats.length} tenantChats, ${combinedChats.length} combinedChats`
        );

        // First, add all tenantChats (Firestore chats loaded via loadTenantChat)
        // These ensure every tenant has a chat entry, even if they don't have a backend conversation
        tenantChats.forEach((chat) => {
          if (chat.tenantId) {
            chatMapForFinal.set(chat.tenantId, chat);
            console.log(
              `[Communication] Added tenantChat for tenant ${chat.tenantId} (${chat.tenantName}): ${chat.id}`
            );
          }
          if (chat.id) {
            chatIdMap.set(chat.id, chat);
          }
        });

        // Then, add/overwrite with combinedChats (backend chats have _fromBackend: true)
        // Backend chats should replace Firestore chats for the same tenant
        // IMPORTANT: If multiple chats exist for the same tenant, merge them intelligently
        combinedChats.forEach((chat) => {
          if (chat.tenantId) {
            const existingChat = chatMapForFinal.get(chat.tenantId);
            if (existingChat) {
              // Multiple chats for same tenant - merge them intelligently
              // Prefer backend chat over Firestore chat
              // Prefer chat with more recent messages
              const shouldReplace =
                (chat._fromBackend && !existingChat._fromBackend) || // Backend > Firestore
                (chat._fromBackend === existingChat._fromBackend &&
                  chat.lastMessageTime &&
                  existingChat.lastMessageTime &&
                  new Date(chat.lastMessageTime) >
                    new Date(existingChat.lastMessageTime)); // More recent

              if (shouldReplace) {
                console.log(
                  `[Communication] Replacing chat ${existingChat.id} with ${chat.id} for tenant ${chat.tenantId} (backend: ${chat._fromBackend}, existing: ${existingChat._fromBackend})`
                );
                chatMapForFinal.set(chat.tenantId, chat);
              } else {
                console.log(
                  `[Communication] Keeping existing chat ${existingChat.id} for tenant ${chat.tenantId} (skipping ${chat.id})`
                );
                // Still add to chatIdMap for lookup by ID
                if (chat.id) {
                  chatIdMap.set(chat.id, chat);
                }
              }
            } else {
              console.log(
                `[Communication] Adding new combinedChat ${chat.id} for tenant ${chat.tenantId} (not in tenantChats)`
              );
              chatMapForFinal.set(chat.tenantId, chat);
            }
          } else {
            // If chat doesn't have tenantId, try to extract it from participants
            const tenantParticipant = chat.participants?.find(
              (p) => p.userType === "tenant"
            );
            if (tenantParticipant?.userId) {
              chat.tenantId = tenantParticipant.userId;
              const existingChat = chatMapForFinal.get(chat.tenantId);
              if (existingChat) {
                // Same logic as above - prefer backend, prefer more recent
                const shouldReplace =
                  (chat._fromBackend && !existingChat._fromBackend) ||
                  (chat._fromBackend === existingChat._fromBackend &&
                    chat.lastMessageTime &&
                    existingChat.lastMessageTime &&
                    new Date(chat.lastMessageTime) >
                      new Date(existingChat.lastMessageTime));

                if (shouldReplace) {
                  console.log(
                    `[Communication] Replacing chat ${existingChat.id} with ${chat.id} for tenant ${chat.tenantId} (extracted from participants)`
                  );
                  chatMapForFinal.set(chat.tenantId, chat);
                } else {
                  console.log(
                    `[Communication] Keeping existing chat ${existingChat.id} for tenant ${chat.tenantId} (skipping ${chat.id} with extracted tenantId)`
                  );
                }
              } else {
                console.log(
                  `[Communication] Adding combinedChat ${chat.id} for tenant ${chat.tenantId} (extracted from participants)`
                );
                chatMapForFinal.set(chat.tenantId, chat);
              }
            } else {
              // If chat doesn't have tenantId and can't extract it, add it by chat ID instead
              if (chat.id) {
                const existingById = chatIdMap.get(chat.id);
                if (!existingById) {
                  console.log(
                    `[Communication] Adding combinedChat ${chat.id} without tenantId (using chat ID as key)`
                  );
                  chatIdMap.set(chat.id, chat);
                }
              }
            }
          }
          if (chat.id) {
            chatIdMap.set(chat.id, chat);
          }
        });

        // Final pass: Ensure all chats with tenantId are properly merged
        // This catches any chats that might have been missed in the previous loops
        combinedChats.forEach((chat) => {
          // If chat doesn't have tenantId, try to extract it from participants
          if (!chat.tenantId) {
            const tenantParticipant = chat.participants?.find(
              (p) => p.userType === "tenant"
            );
            if (tenantParticipant?.userId) {
              chat.tenantId = tenantParticipant.userId;
              const existingChat = chatMapForFinal.get(chat.tenantId);
              if (existingChat) {
                // Prefer backend chat or more recent chat
                const shouldReplace =
                  (chat._fromBackend && !existingChat._fromBackend) ||
                  (chat._fromBackend === existingChat._fromBackend &&
                    chat.lastMessageTime &&
                    existingChat.lastMessageTime &&
                    new Date(chat.lastMessageTime) >
                      new Date(existingChat.lastMessageTime));

                if (shouldReplace) {
                  console.log(
                    `[Communication] Final pass: Replacing chat ${existingChat.id} with ${chat.id} for tenant ${chat.tenantId} (extracted from participants)`
                  );
                  chatMapForFinal.set(chat.tenantId, chat);
                } else {
                  console.log(
                    `[Communication] Final pass: Keeping existing chat ${existingChat.id} for tenant ${chat.tenantId} (skipping ${chat.id})`
                  );
                }
              } else {
                console.log(
                  `[Communication] Final pass: Adding chat ${chat.id} for tenant ${chat.tenantId} (extracted from participants)`
                );
                chatMapForFinal.set(chat.tenantId, chat);
              }
            }
          } else {
            // Chat has tenantId - verify it's in the map and is the best version
            const existingChat = chatMapForFinal.get(chat.tenantId);
            if (!existingChat) {
              // Missing - add it
              console.log(
                `[Communication] Final pass: Adding missing chat ${chat.id} for tenant ${chat.tenantId}`
              );
              chatMapForFinal.set(chat.tenantId, chat);
            } else if (chat._fromBackend && !existingChat._fromBackend) {
              // Backend chat should replace Firestore chat
              console.log(
                `[Communication] Final pass: Replacing Firestore chat ${existingChat.id} with backend chat ${chat.id} for tenant ${chat.tenantId}`
              );
              chatMapForFinal.set(chat.tenantId, chat);
            } else if (
              chat._fromBackend === existingChat._fromBackend &&
              chat.lastMessageTime &&
              existingChat.lastMessageTime &&
              new Date(chat.lastMessageTime) >
                new Date(existingChat.lastMessageTime)
            ) {
              // Same source type, but this one is more recent
              console.log(
                `[Communication] Final pass: Replacing chat ${existingChat.id} with more recent chat ${chat.id} for tenant ${chat.tenantId}`
              );
              chatMapForFinal.set(chat.tenantId, chat);
            }
          }
        });

        console.log(
          `[Communication] After merging: ${chatMapForFinal.size} chats by tenantId, ${chatIdMap.size} chats by chatId`
        );

        // Ensure ALL tenants have a chat entry - create placeholder chats for tenants without chats
        // Also try to fetch last message for placeholder chats
        const placeholderChatPromises = tenants
          .filter((tenant) => !chatMapForFinal.has(tenant.id))
          .map(async (tenant) => {
            console.log(
              `[Communication] Creating placeholder chat for tenant ${tenant.id} (${tenant.name}) - no chat found`
            );

            // Check if there's a cached backend conversation ID for this tenant
            const localStorageKey = chatModel.CONV_KEY(tenant.id);
            const cachedConversationId =
              typeof window !== "undefined"
                ? localStorage.getItem(localStorageKey)
                : null;

            let chatId =
              cachedConversationId || `chat_${tenant.id}_${user.uid}`;
            let isBackendConversation = !!cachedConversationId;
            let lastMessage = "No messages yet";
            let lastMessageTime = "Just now";

            // Try to fetch the last message for this chat
            if (isBackendConversation && cachedConversationId) {
              // Fetch from backend API
              try {
                const apiUrl = process.env.REACT_APP_API_URL;
                if (apiUrl) {
                  const fetchUrl = `${apiUrl}/conversations/${cachedConversationId}?userId=${user.uid}&limit=1000&includeAllMessages=true`;
                  console.log(
                    `[Communication] Fetching last message for placeholder backend conversation ${cachedConversationId} (tenant ${tenant.name})`
                  );

                  const response = await fetch(fetchUrl, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                  });

                  if (response.ok) {
                    const data = await response.json();
                    const fullConversation = data.success
                      ? data.conversation
                      : null;

                    if (
                      fullConversation?.messages &&
                      fullConversation.messages.length > 0
                    ) {
                      const lastMsg =
                        fullConversation.messages[
                          fullConversation.messages.length - 1
                        ];

                      // Handle different message content formats
                      if (
                        typeof lastMsg.content === "object" &&
                        lastMsg.content?.text
                      ) {
                        lastMessage = lastMsg.content.text;
                      } else if (typeof lastMsg.content === "string") {
                        lastMessage = lastMsg.content;
                      } else if (lastMsg.message) {
                        lastMessage = lastMsg.message;
                      }

                      // Get timestamp
                      const timestamp = lastMsg.timestamp || lastMsg.createdAt;
                      if (timestamp) {
                        lastMessageTime = formatTime(new Date(timestamp));
                      }

                      console.log(
                        `[Communication] ✅ Found last message for placeholder backend conversation ${cachedConversationId} (tenant ${
                          tenant.name
                        }): "${lastMessage.substring(0, 50)}${
                          lastMessage.length > 50 ? "..." : ""
                        }"`
                      );
                    }
                  }
                }
              } catch (error) {
                console.warn(
                  `[Communication] ⚠️ Could not fetch last message for placeholder backend conversation ${cachedConversationId} (tenant ${tenant.name}):`,
                  error
                );
              }
            } else {
              // Firestore chat - fetch from Firestore
              try {
                const messages = await chatModel.getMessages(chatId, null, 1); // Get only the last message
                if (messages.length > 0) {
                  const lastMsg = messages[messages.length - 1];
                  lastMessage = lastMsg.content || "No messages yet";
                  lastMessageTime = formatTime(lastMsg.timestamp);
                  console.log(
                    `[Communication] ✅ Found last message for placeholder Firestore chat ${chatId} (tenant ${
                      tenant.name
                    }): "${lastMessage.substring(0, 50)}${
                      lastMessage.length > 50 ? "..." : ""
                    }"`
                  );
                }
              } catch (error) {
                console.log(
                  `[Communication] No messages found for placeholder chat ${chatId} (tenant ${tenant.name}), using default`
                );
              }
            }

            const placeholderChat = {
              id: chatId,
              tenantId: tenant.id,
              tenantName: tenant.name,
              unitNumber: tenant.unitId
                ? units.find((u) => u.id === tenant.unitId)?.unitNumber || "N/A"
                : "N/A",
              unitAddress: getUnitAddress(tenant),
              lastMessage,
              lastMessageTime,
              unreadCount: 0,
              status: "active",
              leoEnabled: true,
              _fromBackend: isBackendConversation,
            };

            return { tenantId: tenant.id, chat: placeholderChat };
          });

        // Wait for all placeholder chats to be created with their last messages
        const placeholderChatResults = await Promise.all(
          placeholderChatPromises
        );
        placeholderChatResults.forEach(({ tenantId, chat }) => {
          chatMapForFinal.set(tenantId, chat);
        });

        console.log(
          `[Communication] After ensuring all tenants have chats: ${chatMapForFinal.size} total chats`
        );

        // Get all chats from chatMapForFinal (these are already merged by tenantId)
        // This ensures we only have ONE chat per tenant
        const allMergedChats = Array.from(chatMapForFinal.values());

        // Log any duplicates by tenantId (shouldn't happen, but check anyway)
        const tenantIdCounts = new Map();
        allMergedChats.forEach((chat) => {
          if (chat.tenantId) {
            tenantIdCounts.set(
              chat.tenantId,
              (tenantIdCounts.get(chat.tenantId) || 0) + 1
            );
          }
        });
        const duplicateTenants = Array.from(tenantIdCounts.entries()).filter(
          ([tenantId, count]) => count > 1
        );
        if (duplicateTenants.length > 0) {
          console.warn(
            `[Communication] ⚠️ Found duplicate chats for tenants:`,
            duplicateTenants.map(
              ([tenantId, count]) => `${tenantId} (${count} chats)`
            )
          );
          // Remove duplicates - keep only the backend chat if available, otherwise keep the first one
          const seenTenantIds = new Set();
          const deduplicatedChats = [];
          allMergedChats.forEach((chat) => {
            if (!chat.tenantId) {
              // Chats without tenantId shouldn't be in chatMapForFinal, but handle it
              console.warn(`[Communication] Chat ${chat.id} has no tenantId`);
              deduplicatedChats.push(chat);
            } else if (!seenTenantIds.has(chat.tenantId)) {
              // First chat for this tenant - add it
              seenTenantIds.add(chat.tenantId);
              deduplicatedChats.push(chat);
            } else {
              // Duplicate - check if this one is from backend and the existing one isn't
              const existingIndex = deduplicatedChats.findIndex(
                (c) => c.tenantId === chat.tenantId
              );
              if (existingIndex >= 0) {
                const existingChat = deduplicatedChats[existingIndex];
                if (chat._fromBackend && !existingChat._fromBackend) {
                  // Replace Firestore chat with backend chat
                  console.log(
                    `[Communication] Replacing Firestore chat ${existingChat.id} with backend chat ${chat.id} for tenant ${chat.tenantId}`
                  );
                  deduplicatedChats[existingIndex] = chat;
                } else {
                  console.log(
                    `[Communication] Skipping duplicate chat ${chat.id} for tenant ${chat.tenantId} (keeping ${existingChat.id})`
                  );
                }
              }
            }
          });

          // Replace allMergedChats with deduplicated version
          allMergedChats.length = 0;
          allMergedChats.push(...deduplicatedChats);
          console.log(
            `[Communication] After deduplication: ${allMergedChats.length} chats`
          );
        }

        // Filter out any maintenance-related chats - they should not appear in messages
        // Maintenance chats are identified by having ticketId, handymanIds, or being maintenance_group type
        // Also normalize all Date objects to strings to prevent React rendering errors
        const regularChats = allMergedChats
          .filter((chat) => {
            // Exclude chats that are maintenance-related
            const isMaintenance =
              chat.type === "maintenance_group" ||
              (chat.ticketId &&
                chat.ticketId !== null &&
                chat.ticketId !== "") ||
              (chat.handymanIds &&
                Array.isArray(chat.handymanIds) &&
                chat.handymanIds.length > 0);

            if (isMaintenance) {
              console.log(
                "[Communication] Filtering out maintenance chat from messages:",
                {
                  id: chat.id,
                  type: chat.type,
                  ticketId: chat.ticketId,
                  handymanIds: chat.handymanIds,
                  chatName: chat.chatName,
                }
              );
            }

            return !isMaintenance;
          })
          .map((chat) => {
            // Normalize all Date objects to strings
            const normalized = { ...chat };
            // Convert createdAt if it's a Date object
            if (normalized.createdAt instanceof Date) {
              normalized.createdAt = normalized.createdAt.toISOString();
            } else if (
              normalized.createdAt &&
              typeof normalized.createdAt === "object" &&
              normalized.createdAt.toDate
            ) {
              // Handle Firestore Timestamp
              normalized.createdAt = normalized.createdAt
                .toDate()
                .toISOString();
            }
            // Convert updatedAt if it's a Date object
            if (normalized.updatedAt instanceof Date) {
              normalized.updatedAt = normalized.updatedAt.toISOString();
            } else if (
              normalized.updatedAt &&
              typeof normalized.updatedAt === "object" &&
              normalized.updatedAt.toDate
            ) {
              // Handle Firestore Timestamp
              normalized.updatedAt = normalized.updatedAt
                .toDate()
                .toISOString();
            }
            // Convert lastMessageTime if it's a Date object
            if (normalized.lastMessageTime instanceof Date) {
              normalized.lastMessageTime = formatTime(
                normalized.lastMessageTime
              );
            } else if (
              normalized.lastMessageTime &&
              typeof normalized.lastMessageTime === "object" &&
              normalized.lastMessageTime.toDate
            ) {
              // Handle Firestore Timestamp
              normalized.lastMessageTime = formatTime(
                normalized.lastMessageTime.toDate()
              );
            }
            return normalized;
          });

        console.log(
          `[Communication] Showing ${regularChats.length} conversations (${
            regularChats.filter((c) => c._fromBackend).length
          } from backend, ${
            regularChats.filter((c) => !c._fromBackend).length
          } from Firestore)`
        );

        // Sort by most recent message (null values go to end; magic-link rows may set _sortKey)
        const chatRecency = (c) => {
          if (typeof c._sortKey === "number" && !Number.isNaN(c._sortKey)) {
            return c._sortKey;
          }
          if (!c.lastMessageTime) return 0;
          if (c.lastMessageTime instanceof Date) {
            return c.lastMessageTime.getTime();
          }
          const d = new Date(c.lastMessageTime);
          return Number.isNaN(d.getTime()) ? 0 : d.getTime();
        };
        const sortedChats = [...regularChats].sort(
          (a, b) => chatRecency(b) - chatRecency(a)
        );

        // Log final chat counts
        // All chats are regular tenant-landlord chats (vendors are added as participants)
        console.log(
          `[Communication] Final sorted chats: ${sortedChats.length} total (all are regular tenant-landlord chats)`
        );

        let listWithPublicLeo = sortedChats;
        try {
          const API_URL =
            process.env.REACT_APP_API_URL || "http://localhost:4000/api";
          const token = await getAuth().currentUser?.getIdToken();
          if (token) {
            const sRes = await fetch(
              `${API_URL}/landlord/public-leo-sessions`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (sRes.ok) {
              const j = await sRes.json();
              const rows = Array.isArray(j.sessions) ? j.sessions : [];
              const extras = rows.map((s) => {
                const u = s.unitId
                  ? (units || []).find((x) => x.id === s.unitId)
                  : null;
                const addr = u ? getUnitDisplayAddress(u) : null;
                const fromUnit =
                  u && typeof u.leoChatVisitorLabel === "string"
                    ? u.leoChatVisitorLabel.trim()
                    : "";
                const nameLine =
                  (fromUnit && fromUnit.slice(0, 200)) || "Guest";
                const updatedAt = s.updatedAt
                  ? new Date(s.updatedAt)
                  : new Date(0);
                const preview =
                  s.lastMessagePreview && String(s.lastMessagePreview).trim()
                    ? String(s.lastMessagePreview)
                    : "No messages yet";
                return {
                  id: `pl-${s.sessionId}`,
                  publicLeoSessionId: s.sessionId,
                  _isPublicLeoSession: true,
                  unitId: s.unitId || null,
                  tenantName: nameLine,
                  chatName: nameLine,
                  unitNumber: u?.unitNumber || addr || "-",
                  lastMessage: preview,
                  lastMessageTime: formatTime(updatedAt),
                  _sortKey: updatedAt.getTime(),
                  leoEnabled: true,
                  status: "active",
                  unreadCount: 0,
                };
              });
              listWithPublicLeo = [...sortedChats, ...extras].sort(
                (a, b) => chatRecency(b) - chatRecency(a)
              );
            }
          }
        } catch (e) {
          console.warn("[Communication] public-leo-sessions list:", e);
        }

        // Update state with all chats (this will include the prioritized chat if it was loaded)
        // If we already set state with just the prioritized chat, this update will add the rest
        setChats(listWithPublicLeo);
        setAllChats(listWithPublicLeo);

        // Mark chats as initialized so we don't reload on tenantId changes
        chatsInitializedRef.current = true;
      } catch (error) {
        console.error("Error initializing tenant chats:", error);
        message.error("Failed to load tenant chats");
        chatsInitializedRef.current = true; // Mark as initialized even on error to prevent retry loops
      } finally {
        setIsInitializingChats(false);
      }
    };

    initializeTenantChats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.uid,
    user?.displayName,
    tenants,
    units,
    formatTime,
    getUnitAddress,
  ]);
  // Note: tenantId is intentionally excluded from dependencies to prevent reloading chats when switching between chats.
  // We only use tenantId to store the initial value in a ref for prioritized loading on first load.

  // Show all chats in a unified list
  useEffect(() => {
    setChats(allChats);
  }, [allChats]);

  // Track whether the user is at the bottom of the messages container
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const nearBottom = scrollHeight - scrollTop - clientHeight <= 5; // tighter threshold
      if (lastIsAtBottomRef.current !== nearBottom) {
        console.log("[CommsChat] scroll-state-change", {
          isAtBottom: nearBottom,
          scrollTop,
          scrollHeight,
          clientHeight,
          timestamp: new Date().toISOString(),
        });
        lastIsAtBottomRef.current = nearBottom;
      }
      setIsAtBottom(nearBottom);
    };

    // Initialize and listen
    handleScroll();
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Forward scroll events from main chat area gray background to messages container
  // Note: Sidebar should scroll independently, so we don't attach listeners to it
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    const mainChatArea = mainChatAreaRef.current;

    if (!messagesContainer || !selectedChat || !mainChatArea) return;

    const handleWheel = (e) => {
      // Only forward if the messages container is scrollable
      if (messagesContainer.scrollHeight > messagesContainer.clientHeight) {
        // Check if we're at the boundaries
        const isAtTop = messagesContainer.scrollTop === 0;
        const isAtBottom =
          messagesContainer.scrollTop + messagesContainer.clientHeight >=
          messagesContainer.scrollHeight - 5;

        // Check if the scroll is happening over the messages container or its children
        // If so, let native scrolling handle it
        const target = e.target;
        const isOverMessagesArea =
          target === messagesContainer || messagesContainer.contains(target);

        // Check if we're over the chat header (should not forward scroll from header)
        const chatHeader = mainChatArea.querySelector('[class*="ChatHeader"]');
        const isOverChatHeader =
          chatHeader && (target === chatHeader || chatHeader.contains(target));

        // Only forward scroll if we're NOT over the messages area or chat header
        // This means we're scrolling on the gray background areas of the main chat area
        if (!isOverMessagesArea && !isOverChatHeader) {
          if ((e.deltaY < 0 && !isAtTop) || (e.deltaY > 0 && !isAtBottom)) {
            e.preventDefault();
            e.stopPropagation();
            messagesContainer.scrollTop += e.deltaY;
          }
        }
      }
    };

    // Only attach to main chat area, NOT the sidebar
    mainChatArea.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      mainChatArea.removeEventListener("wheel", handleWheel);
    };
  }, [selectedChat]);

  // Scroll to repair ticket messages if repairId is provided
  useEffect(() => {
    if (
      !repairIdToScrollTo ||
      !selectedChat ||
      isLoadingMessages ||
      messages.length === 0
    ) {
      return;
    }

    // Wait for messages to render, then find and scroll to repair-related messages
    setTimeout(() => {
      // Find messages related to this repair ticket
      // Check both metadata.repairTicketId and repairTicketId (from backend)
      const repairMessages = messages.filter((msg) => {
        const repairTicketId =
          msg.metadata?.repairTicketId || msg.repairTicketId;
        return repairTicketId === repairIdToScrollTo;
      });

      if (repairMessages.length > 0) {
        // Find the first repair-related message element
        const firstRepairMessage = repairMessages[0];
        const messageElement = document.querySelector(
          `[data-message-id="${firstRepairMessage.id}"]`
        );

        if (messageElement) {
          // Scroll to the message with some offset from top
          messageElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // Highlight the message briefly
          messageElement.style.transition = "background-color 0.3s ease";
          messageElement.style.backgroundColor = "rgba(34, 197, 94, 0.1)";
          setTimeout(() => {
            messageElement.style.backgroundColor = "";
          }, 2000);

          // Clear the repairId state after scrolling
          setRepairIdToScrollTo(null);
        }
      } else {
        // No repair messages found, just scroll to bottom
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "auto" });
        }
        setRepairIdToScrollTo(null);
      }
    }, 100);
  }, [
    repairIdToScrollTo,
    selectedChat,
    isLoadingMessages,
    messages.length,
    messages,
  ]);

  // Jump to bottom immediately when a chat is selected (without smooth scrolling)
  // Only if we're not scrolling to repair messages
  // Only scroll to bottom on initial chat selection, not on every refetch
  useEffect(() => {
    if (!selectedChat || isLoadingMessages || repairIdToScrollTo) return;

    // Only scroll to bottom on initial chat selection (when chat changes)
    // Don't scroll on every message refetch - let the user scroll up to read history
    const shouldScrollToBottom = isAtBottom; // Only if user is already at bottom

    if (shouldScrollToBottom) {
      // Wait for messages to render, then jump to bottom instantly
      setTimeout(() => {
        if (messagesEndRef.current && isAtBottom) {
          messagesEndRef.current.scrollIntoView({ behavior: "auto" });
        }
      }, 50);
    }
    // isAtBottom/selectedChat omitted: we only want this when chat id or loading/repair scroll changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat?.id, isLoadingMessages, repairIdToScrollTo]);

  // Auto-scroll to bottom only if the user is already at (or near) the bottom
  // AND there are actually new messages (not just a refetch of existing messages)
  // Also scroll when pending messages are added
  useEffect(() => {
    const newCount = messages.length;
    const prevCount = prevMessagesCountRef.current;
    const hasNew = newCount > prevCount;

    // Check if there are pending messages for this chat
    const pendingCount = Array.from(pendingMessages.values()).filter(
      (pm) => pm.chatId === selectedChat?.id
    ).length;

    if (hasNew || pendingCount > 0) {
      console.log("[CommsChat] new-messages", {
        previousCount: prevCount,
        newCount,
        pendingCount,
        isAtBottom,
        action: isAtBottom ? "auto-scroll" : "skip",
        timestamp: new Date().toISOString(),
      });

      // Always scroll when there are pending messages (optimistic updates from user)
      // Also scroll if user is at bottom AND we have new messages
      if (pendingCount > 0 || (isAtBottom && hasNew)) {
        // Use setTimeout to ensure DOM is fully updated with the optimistic message
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      }
    }

    // Update the count after checking
    prevMessagesCountRef.current = newCount;
  }, [messages.length, isAtBottom, pendingMessages, selectedChat?.id]); // Include pendingMessages to trigger scroll when optimistic message is added

  // Watch for new messages and immediately remove matching optimistic messages
  useEffect(() => {
    if (!selectedChat || pendingMessages.size === 0) return;

    const pendingMessagesArray = Array.from(pendingMessages.values())
      .filter((pm) => pm.chatId === selectedChat?.id)
      .map((pm) => pm.message);

    if (pendingMessagesArray.length === 0) return;

    const optimisticMessages = pendingMessagesArray.filter(
      (msg) => msg.isOptimistic
    );
    if (optimisticMessages.length === 0) return;

    // Check if any optimistic messages match real messages
    const confirmedOptimisticIds = new Set();

    optimisticMessages.forEach((optMsg) => {
      const optContent =
        typeof optMsg.content === "string" ? optMsg.content.trim() : "";
      if (!optContent) return;

      // Look for matching real message
      const matchingRealMsg = messages.find((realMsg) => {
        if (realMsg.sender !== "landlord") return false;
        if (realMsg.fromUserId && realMsg.fromUserId !== user?.uid)
          return false;

        // Extract content
        let realContent = "";
        if (typeof realMsg.content === "string") {
          realContent = realMsg.content.trim();
        } else if (realMsg.content?.text) {
          realContent = realMsg.content.text.trim();
        } else if (realMsg.message) {
          realContent =
            typeof realMsg.message === "string"
              ? realMsg.message.trim()
              : realMsg.message.text?.trim() || "";
        }

        if (!realContent) return false;

        // Match by content (case-insensitive)
        return optContent.toLowerCase() === realContent.toLowerCase();
      });

      if (matchingRealMsg) {
        console.log(
          "[Communication] useEffect: Found matching real message for optimistic:",
          optMsg.id,
          optContent
        );
        confirmedOptimisticIds.add(optMsg.id);
      }
    });

    // Remove confirmed optimistic messages immediately
    if (confirmedOptimisticIds.size > 0) {
      setPendingMessages((prev) => {
        const newMap = new Map(prev);
        let removedAny = false;
        confirmedOptimisticIds.forEach((id) => {
          if (newMap.has(id)) {
            console.log(
              "[Communication] useEffect: Removing confirmed optimistic message:",
              id
            );
            newMap.delete(id);
            removedAny = true;
          }
        });
        return removedAny ? newMap : prev;
      });
    }
  }, [messages, selectedChat, pendingMessages, user?.uid]); // Watch messages array for changes

  // Request notification permission on component mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Handle real-time message updates and notifications
  useEffect(() => {
    if (!selectedChat) return;

    if (selectedChat._isPublicLeoSession) {
      return;
    }

    // Only subscribe to Firestore chats, not backend conversations
    // Backend conversations use refetchInterval in the query instead
    if (selectedChat._fromBackend) {
      return;
    }

    // Subscribe to real-time message updates
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    unsubscribeRef.current = chatModel.subscribeToMessages(
      selectedChat.id,
      (newMessages) => {
        console.log("Real-time message update:", {
          chatId: selectedChat.id,
          previousCount: messages.length,
          newCount: newMessages.length,
        });

        const previousMessageCount = messages.length;

        // Show notification for new messages from tenants or AI (not from landlord)
        if (newMessages.length > previousMessageCount) {
          const newMessage = newMessages[newMessages.length - 1];
          if (newMessage.sender === "tenant" || newMessage.sender === "ai") {
            const senderName = getSenderName(newMessage.sender);
            showNotification(newMessage, senderName);
          }

          // Refetch messages to update the UI
          refetchMessages();
        }
      }
    );

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
    // getSenderName is defined later in the file; including it would re-subscribe every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat, messages.length, refetchMessages, showNotification]);

  // Update chat list when messages change
  useEffect(() => {
    if (!selectedChat || !messages.length) return;

    const lastMessage = messages[messages.length - 1];
    const newLastMessage = lastMessage.content;
    const newLastMessageTime = formatTime(lastMessage.timestamp);

    setChats((prevChats) =>
      prevChats.map((chat) => {
        if (chat.id === selectedChat.id) {
          // Only update if the message content or time has actually changed
          if (
            chat.lastMessage !== newLastMessage ||
            chat.lastMessageTime !== newLastMessageTime
          ) {
            return {
              ...chat,
              lastMessage: newLastMessage,
              lastMessageTime: newLastMessageTime,
            };
          }
          return chat; // No change needed
        }
        return chat;
      })
    );
  }, [selectedChat, messages, formatTime]);

  const uploadPublicLeoLandlordImage = async (file) => {
    if (!file.type.startsWith("image/")) {
      message.error("Please choose an image file.");
      return null;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error("Image must be smaller than 10 MB.");
      return null;
    }
    const fileName = `ll-${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 9)}_${file.name}`;
    const storageRef = ref(
      storage,
      `public-leo-chat-landlord-images/${fileName}`
    );
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const handlePublicLeoComposeAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 4 - publicLeoPendingImages.length;
    if (remaining <= 0) {
      message.warning("You can attach up to 4 images per message.");
      return;
    }
    const toUpload = files.slice(0, remaining);
    setPublicLeoImageUploading(true);
    try {
      const results = await Promise.all(
        toUpload.map(async (file) => {
          const url = await uploadPublicLeoLandlordImage(file);
          if (!url) return null;
          return {
            url,
            previewUrl: URL.createObjectURL(file),
            name: file.name,
          };
        })
      );
      const valid = results.filter(Boolean);
      if (valid.length) {
        setPublicLeoPendingImages((prev) => [...prev, ...valid]);
      }
    } catch (err) {
      message.error("Failed to upload image. Please try again.");
      console.error("[Communication] public leo upload error:", err);
    } finally {
      setPublicLeoImageUploading(false);
      if (publicLeoFileInputRef.current) {
        publicLeoFileInputRef.current.value = "";
      }
    }
  };

  const removePublicLeoPendingImage = (idx) => {
    setPublicLeoPendingImages((prev) => {
      const next = [...prev];
      if (next[idx]?.previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(next[idx].previewUrl);
      }
      next.splice(idx, 1);
      return next;
    });
  };

  const handleSendMessage = async () => {
    if (!selectedChat) return;

    if (selectedChat._isPublicLeoSession) {
      const text = newMessage.trim();
      if (!text && publicLeoPendingImages.length === 0) return;
      if (!selectedChat.publicLeoSessionId) {
        message.error(
          "Could not find this session. Try re-opening the thread."
        );
        return;
      }
      const imageUrls = publicLeoPendingImages
        .map((p) => p.url)
        .filter(Boolean);
      const tempMessageId = `temp-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const optimisticMessage = {
        id: tempMessageId,
        sender: "landlord",
        fromUserType: "landlord",
        fromUserId: user?.uid,
        content: text,
        timestamp: new Date(),
        images: imageUrls,
        metadata: {},
        isSending: true,
        isOptimistic: true,
      };
      publicLeoPendingImages.forEach((p) => {
        if (p.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(p.previewUrl);
        }
      });
      setPublicLeoPendingImages([]);
      setNewMessage("");
      setPendingMessages((prev) => {
        const newMap = new Map(prev);
        newMap.set(tempMessageId, {
          message: optimisticMessage,
          chatId: selectedChat.id,
          isSending: true,
        });
        return newMap;
      });
      try {
        setIsComposing(true);
        const API_URL =
          process.env.REACT_APP_API_URL || "http://localhost:4000/api";
        const token = await getAuth().currentUser?.getIdToken();
        if (!token) {
          throw new Error("Not signed in");
        }
        const res = await fetch(
          `${API_URL}/landlord/public-leo-sessions/${encodeURIComponent(
            selectedChat.publicLeoSessionId
          )}/messages`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ text, images: imageUrls }),
          }
        );
        const data = await res.json().catch(() => ({}));
        if (res.status === 410) {
          message.warning(
            data.error ||
              "This session has ended. Refresh the list and open a current thread."
          );
          setPendingMessages((prev) => {
            const next = new Map(prev);
            next.delete(tempMessageId);
            return next;
          });
          return;
        }
        if (!res.ok) {
          throw new Error(data.error || "Failed to send message");
        }
        await refetchMessages();
        setPendingMessages((prev) => {
          const next = new Map(prev);
          next.delete(tempMessageId);
          return next;
        });
        trackEvent("landlord_public_leo_message_sent", {
          sessionId: selectedChat.publicLeoSessionId,
          hasText: !!text,
          imageCount: imageUrls.length,
        });
      } catch (err) {
        console.error("[Communication] public leo landlord send error:", err);
        message.error(
          err?.message && err.message.length < 200
            ? err.message
            : "Could not send your message. Please try again."
        );
        setPendingMessages((prev) => {
          const next = new Map(prev);
          next.delete(tempMessageId);
          return next;
        });
        setNewMessage(text);
        setPublicLeoPendingImages(
          imageUrls.map((url, i) => ({
            url,
            previewUrl: url,
            name: `image-${i}`,
          }))
        );
      } finally {
        setIsComposing(false);
      }
      return;
    }

    if (!newMessage.trim()) return;

    // Store message content before clearing
    const messageContent = newMessage.trim();
    const tempMessageId = `temp-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    // IMPORTANT: Re-check for backend chat before sending, in case chats were merged after selection
    // Look up the backend version of the chat to ensure we use the correct conversation ID
    let chatToUse = selectedChat;
    if (selectedChat.tenantId) {
      const backendChat = chats.find(
        (c) => c.tenantId === selectedChat.tenantId && c._fromBackend === true
      );
      if (backendChat) {
        console.log(
          "[Communication] handleSendMessage: Found backend chat, using it instead:",
          {
            originalChatId: selectedChat.id,
            backendChatId: backendChat.id,
            originalHasFromBackend: selectedChat._fromBackend,
            backendHasFromBackend: backendChat._fromBackend,
          }
        );
        chatToUse = backendChat;
        // Update selectedChat to use the backend version
        setSelectedChat(backendChat);
      }
    }

    // Create optimistic message
    const optimisticMessage = {
      id: tempMessageId,
      sender: "landlord",
      fromUserType: "landlord",
      fromUserId: user?.uid,
      content: messageContent,
      timestamp: new Date(),
      images: [],
      metadata: {},
      isSending: true, // Flag to show spinner
      isOptimistic: true, // Flag to identify optimistic messages
    };

    // Add optimistic message to pending messages
    setPendingMessages((prev) => {
      const newMap = new Map(prev);
      newMap.set(tempMessageId, {
        message: optimisticMessage,
        chatId: chatToUse.id,
        isSending: true,
      });
      return newMap;
    });

    // Clear input immediately for better UX
    setNewMessage("");

    try {
      setIsComposing(true);

      // Track message sent
      trackEvent("landlord_message_sent", {
        chatId: chatToUse.id,
        tenantId: chatToUse.tenantId,
        messageLength: messageContent.length,
      });

      // Check if this is a backend conversation
      if (chatToUse._fromBackend) {
        // Send message via backend API
        const API_URL =
          process.env.REACT_APP_API_URL || "http://localhost:4000/api";
        if (!API_URL) {
          throw new Error("REACT_APP_API_URL not configured");
        }

        console.log(
          "🚨🚨🚨 [Communication] LANDLORD SENDING MESSAGE - RECORD THIS INFO:",
          {
            conversationId: chatToUse.id,
            tenantId: chatToUse.tenantId, // CRITICAL: Log tenantId to verify matching
            tenantName: chatToUse.tenantName,
            landlordId: user.uid,
            messageText: messageContent,
            timestamp: new Date().toISOString(),
            chatType: chatToUse.type,
            isBackendConversation: chatToUse._fromBackend,
            participants: chatToUse.participants,
            allChatsForThisTenant: chats
              .filter((c) => c.tenantId === chatToUse.tenantId)
              .map((c) => ({
                id: c.id,
                _fromBackend: c._fromBackend,
                type: c.type,
              })),
          }
        );

        console.warn(
          "⚠️ DIAGNOSTIC: Copy this conversationId and check if tenant is viewing the SAME conversationId:",
          chatToUse.id
        );

        const response = await fetch(`${API_URL}/conversations/send-message`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            conversationId: chatToUse.id,
            fromUserId: user.uid,
            fromUserType: "landlord",
            message: messageContent,
            images: [], // TODO: Support images if needed
          }),
        });

        const responseData = await response.json().catch(() => ({}));
        console.log(
          "✅✅✅ [Communication] LANDLORD MESSAGE SENT - BACKEND RESPONSE:",
          {
            status: response.status,
            success: responseData.success,
            messageId: responseData.messageId,
            conversationId: responseData.conversationId,
            sentConversationId: chatToUse.id,
            conversationIdMatch: responseData.conversationId === chatToUse.id,
            fromUserType: "landlord",
            messageText: messageContent.substring(0, 50),
            timestamp: new Date().toISOString(),
            fullResponse: responseData,
          }
        );

        // MHMDA: 200 + consent_required: show consent modal, roll back optimistic message
        if (
          responseData.consent_required === true ||
          responseData.consentRequired === true
        ) {
          setPendingMessages((prev) => {
            const next = new Map(prev);
            next.delete(tempMessageId);
            return next;
          });
          setHealthConsentModalVisible(true);
          setIsComposing(false);
          return;
        }

        if (responseData.success) {
          console.log("✅ DIAGNOSTIC: Message sent successfully. Now check:");
          console.log(
            "1. Is message in Firestore? (Check conversations/" +
              chatToUse.id +
              "/messages)"
          );
          console.log(
            "2. Does tenant see it? (Check tenant console for conversationId:",
            chatToUse.id
          );
          console.log("3. Tenant should see landlord message count > 0");
        } else {
          console.error(
            "❌ DIAGNOSTIC: Message send FAILED! Backend returned success: false"
          );
        }

        // Log warning if conversationId doesn't match
        if (
          responseData.conversationId &&
          responseData.conversationId !== chatToUse.id
        ) {
          console.warn(
            "[Communication] ⚠️ Conversation ID mismatch! Sent to:",
            chatToUse.id,
            "Backend returned:",
            responseData.conversationId
          );
        }

        if (!response.ok) {
          const error = await response
            .json()
            .catch(() => ({ error: "Unknown error" }));
          throw new Error(
            error.error || `Failed to send message: ${response.status}`
          );
        }

        // Invalidate and refetch messages to show the new message immediately
        // Use the exact query keys that match the useQuery definitions
        console.log(
          "[Communication] Invalidating queries after message send:",
          {
            conversationId: chatToUse.id,
            userId: user?.uid,
            queryKeys: [
              ["backendConversation", chatToUse.id, user?.uid],
              ["backendConversationData", chatToUse.id, user?.uid],
            ],
          }
        );

        await queryClient.invalidateQueries({
          queryKey: ["backendConversation", chatToUse.id, user?.uid],
        });
        await queryClient.invalidateQueries({
          queryKey: ["backendConversationData", chatToUse.id, user?.uid],
        });

        // Also explicitly refetch both queries to ensure they update immediately
        // The messages query depends on backendConversationData, so we need to refetch both
        // Check if refetch functions are available (they should be from the useQuery hooks)
        if (refetchConversation && refetchMessages) {
          console.log(
            "[Communication] Refetching conversation and messages after send"
          );
          await refetchConversation(); // Refetch backendConversationData first
          await refetchMessages(); // Then refetch messages
        } else {
          console.warn(
            "[Communication] Refetch functions not available, relying on invalidation only"
          );
        }

        // Remove optimistic message after a short delay to allow backend to process
        // The real message should appear in the refetched messages
        setTimeout(() => {
          setPendingMessages((prev) => {
            const newMap = new Map(prev);
            if (newMap.has(tempMessageId)) {
              console.log(
                "[Communication] Removing optimistic message after successful send:",
                tempMessageId
              );
              newMap.delete(tempMessageId);
            }
            return newMap;
          });
          // Refetch again after delay to ensure message appears for all users (tenant, landlord, etc.)
          if (refetchConversation && refetchMessages) {
            console.log(
              "[Communication] Refetching again after delay to ensure message appears for all users"
            );
            refetchConversation();
            refetchMessages();
          }
        }, 1000); // Wait 1 second for backend to process and message to appear in refetch
      } else {
        // Send message to Firestore
        console.log(
          "[Communication] Sending landlord message to Firestore (not backend):",
          {
            chatId: chatToUse.id,
            tenantId: chatToUse.tenantId,
            messageText: messageContent,
            reason: "Not a backend conversation",
            _fromBackend: chatToUse._fromBackend,
            chatsCount: chats.length,
            backendChatsInChats: chats.filter((c) => c._fromBackend === true)
              .length,
            chatsForThisTenant: chats
              .filter((c) => c.tenantId === chatToUse.tenantId)
              .map((c) => ({ id: c.id, _fromBackend: c._fromBackend })),
          }
        );

        await chatModel.addMessage(chatToUse.id, "landlord", messageContent);

        console.log("[Communication] Message added to Firestore successfully");

        // Invalidate and refetch messages to show the new message immediately
        await queryClient.invalidateQueries({
          queryKey: chatKeys.messages(chatToUse.id),
        });

        // Remove optimistic message after a short delay to allow Firestore to process
        // The real message should appear in the refetched messages
        setTimeout(() => {
          setPendingMessages((prev) => {
            const newMap = new Map(prev);
            if (newMap.has(tempMessageId)) {
              console.log(
                "[Communication] Removing optimistic message after successful send (Firestore):",
                tempMessageId
              );
              newMap.delete(tempMessageId);
            }
            return newMap;
          });
        }, 1000); // Wait 1 second for Firestore to process and message to appear in refetch
      }

      // message.success('Message sent successfully!'); // Commented out - only show notifications on errors
    } catch (error) {
      console.error("Error sending message:", error);
      message.error("Failed to send message");

      // Remove optimistic message on error
      setPendingMessages((prev) => {
        const newMap = new Map(prev);
        newMap.delete(tempMessageId);
        return newMap;
      });

      // Restore message to input so user can retry
      setNewMessage(messageContent);
    } finally {
      setIsComposing(false);
    }
  };

  const handleChatSelect = useCallback(
    async (chat) => {
      console.log("[Communication] handleChatSelect called:", {
        chatId: chat.id,
        tenantId: chat.tenantId,
        tenantName: chat.tenantName,
        _fromBackend: chat._fromBackend,
        hasVendors: chat.participants?.some(
          (p) => p.userType === "vendor" || p.userType === "handyman"
        ),
        vendorCount:
          chat.participants?.filter(
            (p) => p.userType === "vendor" || p.userType === "handyman"
          ).length || 0,
      });

      // IMPORTANT: Look up the chat from the chats array to get the backend version if it exists
      // This ensures we use the version with _fromBackend: true if available
      // Match by tenantId first (most reliable), then by chat ID
      // Backend and Firestore may have different IDs for the same tenant conversation
      // Use 'chats' instead of 'allChats' because 'chats' contains the merged version with backend chats
      let backendChat = null;

      // First, try to find backend chat by tenantId (most reliable match)
      if (chat.tenantId) {
        backendChat = chats.find(
          (c) => c.tenantId === chat.tenantId && c._fromBackend === true
        );
      }

      // If not found by tenantId, try by chat ID (but only if it has _fromBackend flag)
      if (!backendChat && chat.id) {
        const chatById = chats.find((c) => c.id === chat.id);
        if (chatById && chatById._fromBackend === true) {
          backendChat = chatById;
        }
      }

      const chatToSelect = backendChat || chat;

      console.log("[Communication] Chat selection - using backend version?", {
        originalChatId: chat.id,
        originalTenantId: chat.tenantId,
        originalHasFromBackend: chat._fromBackend,
        backendChatFound: !!backendChat,
        backendChatId: backendChat?.id,
        backendChatTenantId: backendChat?.tenantId,
        backendChatHasFromBackend: backendChat?._fromBackend,
        finalChatId: chatToSelect.id,
        finalChatHasFromBackend: chatToSelect._fromBackend,
        finalChatHasVendors: chatToSelect.participants?.some(
          (p) => p.userType === "vendor" || p.userType === "handyman"
        ),
        chatsCount: chats.length,
        chatsWithFromBackend: chats.filter((c) => c._fromBackend === true)
          .length,
        chatsForThisTenant: chats
          .filter((c) => c.tenantId === chat.tenantId)
          .map((c) => ({ id: c.id, _fromBackend: c._fromBackend })),
      });

      setSelectedChat(chatToSelect);
      setIsSidebarOpen(false); // Close sidebar on mobile when chat is selected
      setShowDetailedInfo(false); // Reset detailed info when switching chats

      // Scroll messages container to top when switching chats
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = 0;
      }

      // Update URL to include tenantId (only if different from current URL)
      if (chat.tenantId && chat.tenantId !== tenantId) {
        navigate(`/communication/${chat.tenantId}`, { replace: true });
      }

      // Mark messages as read (only for Firestore chats, not backend conversations)
      if (!chatToSelect._fromBackend) {
        try {
          await chatModel.markMessagesAsRead(chatToSelect.id, user.uid);

          // Update chat unread count
          setChats((prevChats) =>
            prevChats.map((c) =>
              c.id === chatToSelect.id ? { ...c, unreadCount: 0 } : c
            )
          );
        } catch (error) {
          console.error("Error marking messages as read:", error);
        }
      } else {
        // For backend conversations, just update the unread count locally
        setChats((prevChats) =>
          prevChats.map((c) =>
            c.id === chatToSelect.id ? { ...c, unreadCount: 0 } : c
          )
        );
      }

      trackEvent("chat_selected", {
        chatId: chatToSelect.id,
        tenantId: chatToSelect.tenantId,
      });
    },
    [navigate, tenantId, user?.uid, chats]
  );

  // Handle URL parameter for specific tenant chat (must be after handleChatSelect definition)
  useEffect(() => {
    // Reset ref when tenantId changes
    if (hasSelectedFromUrlRef.current !== tenantId) {
      hasSelectedFromUrlRef.current = null;
    }

    if (!tenantId || !chats.length) {
      return;
    }

    // Only select if we don't already have this chat selected
    if (selectedChat?.tenantId === tenantId) {
      hasSelectedFromUrlRef.current = tenantId;
      return;
    }

    // Prevent multiple selections for the same tenantId
    if (hasSelectedFromUrlRef.current === tenantId) {
      return;
    }

    const targetChat = chats.find((chat) => chat.tenantId === tenantId);
    if (targetChat) {
      hasSelectedFromUrlRef.current = tenantId;
      handleChatSelect(targetChat);
    }
  }, [tenantId, chats.length, chats, selectedChat?.tenantId, handleChatSelect]);

  // Scroll to top when selected chat changes
  useEffect(() => {
    if (selectedChat && messagesContainerRef.current) {
      // Small delay to ensure DOM is updated
      const timeoutId = setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = 0;
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedChat?.id, selectedChat]);

  // Handle chat selection from navigation state (e.g., from repair ticket)
  useEffect(() => {
    const selectChatId = location.state?.selectChatId;
    const repairId = location.state?.repairId;

    // Store repairId if provided for scrolling
    if (repairId) {
      setRepairIdToScrollTo(repairId);
    }

    // Wait for chats to be initialized before trying to select
    if (selectChatId && !isInitializingChats && allChats.length > 0) {
      // Find the chat by ID
      const targetChat = allChats.find((chat) => chat.id === selectChatId);
      if (targetChat && selectedChat?.id !== selectChatId) {
        handleChatSelect(targetChat);
      }
      // Clear the state to prevent re-selecting on re-renders, but keep repairId for scrolling
      navigate(location.pathname, {
        replace: true,
        state: repairId ? { repairId } : {},
      });
    } else if (repairId && !selectChatId) {
      // If repairId is provided but no selectChatId, just clear the state but keep repairId for scrolling
      navigate(location.pathname, { replace: true, state: { repairId } });
    }
  }, [
    location.state,
    isInitializingChats,
    allChats.length,
    allChats,
    selectedChat?.id,
    handleChatSelect,
    navigate,
    location.pathname,
  ]);

  const getSenderName = (sender, message = null) => {
    // If message has fromUserId and fromUserType is vendor, try to get name from participants
    if (sender === "vendor" && message && message.fromUserId) {
      const vendorParticipant = conversationParticipants.find(
        (p) =>
          p.userId === message.fromUserId &&
          (p.userType === "vendor" || p.userType === "handyman")
      );
      if (vendorParticipant) {
        return (
          vendorParticipant.userName ||
          vendorParticipant.userEmail ||
          "Service Team"
        );
      }
      return "Service Team"; // Fallback if participant not found
    }

    switch (sender) {
      case "landlord":
        return "You";
      case "ai":
        return "Leo AI";
      case "tenant":
        return selectedChat?.tenantName || "Tenant";
      case "vendor":
        return "Service Team";
      default:
        return "Unknown";
    }
  };

  const getSenderIcon = (sender) => {
    switch (sender) {
      case "landlord":
        return (
          <UserOutlined
            style={{ filter: "brightness(0) invert(1)", scale: "0.8" }}
          />
        );
      case "ai":
        return (
          <img
            src="/icons/leo/leo.png"
            alt="Leo AI"
            style={{
              width: "16px",
              height: "16px",
              filter: "brightness(0) invert(1)",
            }}
          />
        );
      case "tenant":
        return (
          <HomeOutlined
            style={{ filter: "brightness(0) invert(1)", scale: "0.8" }}
          />
        );
      case "vendor":
        return (
          <ToolOutlined
            style={{ filter: "brightness(0) invert(1)", scale: "0.8" }}
          />
        );
      default:
        return (
          <UserOutlined
            style={{ filter: "brightness(0) invert(1)", scale: "0.8" }}
          />
        );
    }
  };

  const totalUnreadMessages = chats.reduce(
    (sum, chat) => sum + chat.unreadCount,
    0
  );
  const activeChats = chats.filter((chat) => chat.status === "active").length;

  const handleBackClick = () => {
    // Track back button usage
    trackEvent("communication_back_clicked", {
      hasSelectedChat: !!selectedChat,
      tenantId: selectedChat?.tenantId,
    });

    // Navigate to chat list (remove tenantId from URL)
    navigate("/dashboard");
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const isOpsOnlyPlan = isLimitedToOpsChatExperience(userInfo);
  const noRentRollTenants = tenants.length === 0;
  /**
   * Full-OS “add tenants” empty state: only when there is no conversation list yet.
   * Do not use `tenants.length === 0` alone - backend/API conversations can exist while the
   * rent-roll `tenants` array is still empty, which would otherwise hide the thread panel
   * even with a selected chat.
   */
  const showFullOsNoTenantEmpty =
    noRentRollTenants && !isOpsOnlyPlan && chats.length === 0;
  /** Full-page “set up tenant chat” (Ops) only when there is nothing to list yet, including magic-link sessions */
  const showOpsNoRentRollEmpty =
    noRentRollTenants &&
    isOpsOnlyPlan &&
    !isInitializingChats &&
    chats.length === 0;

  return (
    <ChatContainer>
      {/* Mobile Header */}
      <MobileHeader>
        <MobileHeaderRow>
          <MobileBackArrow onClick={handleBackClick}>
            <ArrowLeftOutlined />
          </MobileBackArrow>
          <MobileCenterText>Conversations</MobileCenterText>
          <MobileMenuButton onClick={handleSidebarToggle}>
            <MessageOutlined />
          </MobileMenuButton>
        </MobileHeaderRow>
      </MobileHeader>

      {/* Mobile Overlay */}
      <MobileOverlay
        $isOpen={isSidebarOpen}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* Sidebar */}
      <Sidebar $isOpen={isSidebarOpen} ref={sidebarRef}>
        <SidebarHeader>
          <SidebarHeaderRow>
            <SidebarBackArrow onClick={handleBackClick}>
              <ArrowLeftOutlined />
            </SidebarBackArrow>
            <SidebarSubtitle>Conversations</SidebarSubtitle>
          </SidebarHeaderRow>
        </SidebarHeader>

        <ChatList>
          {isTenantsLoading || isInitializingChats ? (
            <LoadingContainer>
              <Spin size="large" />
              <LoadingText>Loading conversations…</LoadingText>
            </LoadingContainer>
          ) : chats.length === 0 ? (
            <EmptyContainer>
              <EmptyStateIcon>
                <MessageOutlined />
              </EmptyStateIcon>
              <EmptyText>No conversations yet</EmptyText>
              <EmptySubtext>
                {isOpsOnlyPlan
                  ? "Enable tenant chat in Settings and share the link; threads appear here"
                  : "Add tenants to start conversations"}
              </EmptySubtext>
            </EmptyContainer>
          ) : (
            chats.map((chat) => (
              <ChatItem
                key={chat.id}
                onClick={() => handleChatSelect(chat)}
                className={selectedChat?.id === chat.id ? "active" : ""}
                $hasError={chat.status === "error"}
              >
                <ChatAvatar>
                  <Avatar
                    size="small"
                    icon={
                      <UserOutlined
                        style={{ filter: "brightness(0) invert(0)" }}
                      />
                    }
                  />
                  {chat.unreadCount > 0 && (
                    <UnreadBadge count={chat.unreadCount} />
                  )}
                </ChatAvatar>
                <ChatContent>
                  <ChatName $hasError={chat.status === "error"}>
                    {chat.chatName && chat.chatName !== chat.tenantName
                      ? chat.chatName
                      : chat.tenantName}
                  </ChatName>
                  <ChatUnit>
                    {chat.unitNumber ||
                      (chat.ticketTitle
                        ? `Ticket: ${chat.ticketTitle}`
                        : "N/A")}
                  </ChatUnit>
                  <ChatPreview $hasError={chat.status === "error"}>
                    {chat.lastMessage}
                  </ChatPreview>
                </ChatContent>
                <ChatTime>{chat.lastMessageTime}</ChatTime>
              </ChatItem>
            ))
          )}
        </ChatList>

        <SidebarFooter>
          <SidebarStats>
            <StatItem>
              <StatIcon>
                <MessageOutlined />
              </StatIcon>
              <StatContent>
                <StatValue>{activeChats}</StatValue>
                <StatLabel>Active</StatLabel>
              </StatContent>
            </StatItem>
            <StatItem>
              <StatIcon>
                <BellOutlined />
              </StatIcon>
              <StatContent>
                <StatValue>{totalUnreadMessages}</StatValue>
                <StatLabel>Unread</StatLabel>
              </StatContent>
            </StatItem>
            <StatItem>
              <StatIcon>
                <TeamOutlined />
              </StatIcon>
              <StatContent>
                <StatValue>{tenants.length}</StatValue>
                <StatLabel>Tenants</StatLabel>
              </StatContent>
            </StatItem>
          </SidebarStats>
          <div
            style={{
              textAlign: "center",
              color: "#6b7280",
              fontSize: "0.9rem",
              marginTop: "1rem",
            }}
          >
            {isOpsOnlyPlan
              ? "Turn on tenant chat in Settings, then your magic-link threads show here"
              : tenants.length === 0
              ? "Add tenants to start conversations"
              : "Chats for each tenant on your rent roll"}
          </div>
        </SidebarFooter>
      </Sidebar>

      {/* Main Chat Area */}
      <MainChatArea ref={mainChatAreaRef}>
        {showFullOsNoTenantEmpty ? (
          <EmptyState>
            <EmptyStateIcon>
              <MessageOutlined />
            </EmptyStateIcon>
            <EmptyStateTitle>Messages</EmptyStateTitle>
            <EmptyStateSubtitle>
              Add tenants to your properties to start conversations. Each tenant
              on your rent roll can have a landlord-tenant thread here once you
              add them.
            </EmptyStateSubtitle>
          </EmptyState>
        ) : showOpsNoRentRollEmpty ? (
          <EmptyState>
            <EmptyStateIcon>
              <MessageOutlined />
            </EmptyStateIcon>
            <EmptyStateTitle>Tenant chat</EmptyStateTitle>
            <EmptyStateSubtitle>
              {units.length > 0 ? (
                <>
                  Chats are not created for you automatically. In{" "}
                  <strong>Settings → Tenant chat</strong>, turn on chat for a
                  door, add your tenant’s name, then copy the magic link. When
                  they message Leo, the thread appears here.
                </>
              ) : (
                <>
                  Add a door on <strong>Doors</strong> first, then in{" "}
                  <strong>Settings → Tenant chat</strong> enable the link for
                  that door.
                </>
              )}
            </EmptyStateSubtitle>
            {units.length > 0 ? (
              <EmptyStateAction>
                <Button
                  type="primary"
                  onClick={() => navigate("/settings#tenant-chat")}
                >
                  Open tenant chat settings
                </Button>
              </EmptyStateAction>
            ) : (
              <EmptyStateAction>
                <Button type="primary" onClick={() => navigate("/units")}>
                  Add a door
                </Button>
              </EmptyStateAction>
            )}
          </EmptyState>
        ) : selectedChat ? (
          <>
            {/* Chat Header */}
            <ChatHeader>
              <ChatInfo>
                {selectedChat && (
                  <CompactTenantInfo>
                    {/* Essential Info - Always Visible */}

                    <EssentialInfo>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.25rem",
                        }}
                      >
                        <TenantName>
                          {selectedChat?._isPublicLeoSession ? (
                            selectedChat.tenantName || "Guest"
                          ) : isSelectedTenantLoading ? (
                            <Spin size="small" />
                          ) : (
                            selectedTenantData?.name || "Loading..."
                          )}
                        </TenantName>
                        {selectedChat?._isPublicLeoSession ? (
                          <TenantAddress>
                            {selectedChat.unitId
                              ? getUnitDisplayAddress(
                                  units.find(
                                    (u) => u.id === selectedChat.unitId
                                  ) || {}
                                ) || "-"
                              : "-"}
                          </TenantAddress>
                        ) : (
                          !isSelectedTenantLoading &&
                          selectedTenantData && (
                            <TenantAddress>
                              {selectedTenantData.unitAddress}
                            </TenantAddress>
                          )
                        )}
                      </div>

                      {/* Participant List */}
                      <ParticipantsList>
                        <ParticipantItem>
                          <ParticipantTags>
                            <ParticipantIconTag>
                              <TeamOutlined />
                            </ParticipantIconTag>
                            <ParticipantTag>You</ParticipantTag>
                            <ParticipantTag>
                              {selectedChat?.tenantName || "Tenant"}
                            </ParticipantTag>
                            <ParticipantTagWithToggle>
                              <img
                                src="/icons/leo/leo.png"
                                alt="Leo AI"
                                style={{
                                  marginRight: "4px",
                                  width: "12px",
                                  height: "12px",
                                  filter: "brightness(0) invert(0)",
                                }}
                              />
                              Leo AI
                              {!selectedChat?._isPublicLeoSession && (
                                <LeoToggle
                                  size="small"
                                  checked={selectedChat?.leoEnabled !== false}
                                  onChange={async (checked) => {
                                    try {
                                      // Update Firestore chat document
                                      await chatModel.updateChat(
                                        selectedChat.id,
                                        { leoEnabled: checked }
                                      );

                                      // Update local state immediately for UI responsiveness
                                      setChats((prevChats) =>
                                        prevChats.map((c) =>
                                          c.id === selectedChat.id
                                            ? { ...c, leoEnabled: checked }
                                            : c
                                        )
                                      );
                                      setSelectedChat((prev) => ({
                                        ...prev,
                                        leoEnabled: checked,
                                      }));

                                      // Invalidate queries to ensure backend syncs
                                      queryClient.invalidateQueries({
                                        queryKey: ["chat", selectedChat.id],
                                      });
                                      queryClient.invalidateQueries({
                                        queryKey: ["chats", user?.uid],
                                      });

                                      message.success(
                                        checked
                                          ? "Leo AI enabled"
                                          : "Leo AI disabled"
                                      );
                                    } catch (error) {
                                      console.error(
                                        "Error toggling Leo:",
                                        error
                                      );
                                      message.error(
                                        "Failed to update Leo AI setting"
                                      );
                                    }
                                  }}
                                />
                              )}
                            </ParticipantTagWithToggle>
                            {/* Show vendors from backend conversation participants or from handymen */}
                            {(() => {
                              const vendorParticipants = conversationParticipants.filter(
                                (p) =>
                                  p.userType === "vendor" ||
                                  p.userType === "handyman"
                              );

                              console.log(
                                "[Communication] Rendering participants - vendors:",
                                {
                                  totalParticipants:
                                    conversationParticipants.length,
                                  vendorCount: vendorParticipants.length,
                                  vendors: vendorParticipants.map((v) => ({
                                    userId: v.userId,
                                    userName: v.userName,
                                    userType: v.userType,
                                  })),
                                  isBackendConversation,
                                  backendConversationData: !!backendConversationData,
                                  chatHandymenCount: chatHandymen.length,
                                }
                              );

                              if (vendorParticipants.length > 0) {
                                return vendorParticipants.map((vendor) => {
                                  const isDisabled = disabledParticipants.includes(
                                    vendor.userId
                                  );
                                  const canManage = isLandlordInConversation;

                                  return (
                                    <ParticipantTagWithToggle
                                      key={vendor.userId}
                                    >
                                      <ToolOutlined
                                        style={{
                                          marginRight: "4px",
                                          fontSize: "0.7rem",
                                        }}
                                      />
                                      <span
                                        style={{
                                          opacity: isDisabled ? 0.6 : 1,
                                        }}
                                      >
                                        {vendor.userName ||
                                          vendor.userEmail ||
                                          "Service Team"}
                                      </span>
                                      {isDisabled && (
                                        <Badge
                                          count="Disabled"
                                          style={{
                                            backgroundColor: "#ff4d4f",
                                            marginLeft: "4px",
                                            fontSize: "0.65rem",
                                            padding: "0 4px",
                                            height: "16px",
                                            lineHeight: "16px",
                                          }}
                                        />
                                      )}
                                      {canManage && (
                                        <LeoToggle
                                          size="small"
                                          checked={!isDisabled}
                                          onChange={(checked) =>
                                            handleToggleParticipant(
                                              vendor.userId,
                                              checked
                                            )
                                          }
                                        />
                                      )}
                                    </ParticipantTagWithToggle>
                                  );
                                });
                              }

                              // Fallback: Show handymen if no backend participants (for Firestore chats)
                              if (
                                !isBackendConversation &&
                                chatHandymen.length > 0
                              ) {
                                return chatHandymen.map((handyman) => (
                                  <ParticipantTag key={handyman.id}>
                                    <ToolOutlined
                                      style={{
                                        marginRight: "4px",
                                        fontSize: "0.7rem",
                                      }}
                                    />
                                    {handyman.name}
                                  </ParticipantTag>
                                ));
                              }

                              return null;
                            })()}
                          </ParticipantTags>
                        </ParticipantItem>
                      </ParticipantsList>

                      {/* Action Buttons Row - Desktop: inline, Mobile: below */}
                      {!isSelectedTenantLoading && selectedTenantData && (
                        <ActionButtonsRow>
                          <DetailedInfoToggle
                            onClick={() =>
                              setShowDetailedInfo(!showDetailedInfo)
                            }
                          >
                            <InfoIcon $expanded={showDetailedInfo}>
                              <InfoCircleOutlined />
                            </InfoIcon>
                            <ToggleText>
                              {showDetailedInfo
                                ? "Hide Details"
                                : "Show Details"}
                            </ToggleText>
                          </DetailedInfoToggle>

                          <NotificationSettings>
                            {notificationPermission === "default" && (
                              <NotificationButton
                                onClick={requestNotificationPermission}
                              >
                                <BellOutlined /> Enable Notifications
                              </NotificationButton>
                            )}
                            {notificationPermission === "denied" && (
                              <NotificationStatus>
                                <BellOutlined style={{ opacity: 0.5 }} />{" "}
                                Notifications blocked
                              </NotificationStatus>
                            )}
                            {notificationPermission === "granted" && (
                              <NotificationStatus>
                                <BellFilled style={{ scale: 0.8 }} />{" "}
                                {!isMobile
                                  ? "Notifications Enabled"
                                  : "Enabled"}
                              </NotificationStatus>
                            )}
                          </NotificationSettings>
                        </ActionButtonsRow>
                      )}
                    </EssentialInfo>

                    {/* Collapsible Detailed Info */}
                    {!isSelectedTenantLoading &&
                      selectedTenantData &&
                      showDetailedInfo && (
                        <DetailedInfo>
                          {/* Financial Information */}
                          <InfoSection>
                            <SectionTitle>Financial</SectionTitle>
                            <InfoRow>
                              <InfoItem>
                                <InfoLabel>Rent</InfoLabel>
                                <InfoValue>
                                  $
                                  {selectedTenantData.monthlyRent?.toLocaleString() ||
                                    "N/A"}
                                </InfoValue>
                              </InfoItem>
                              <InfoItem>
                                <InfoLabel>Due Day</InfoLabel>
                                <InfoValue>
                                  {selectedTenantData.rentDueDay
                                    ? `${getOrdinalSuffix(
                                        selectedTenantData.rentDueDay
                                      )} of the month`
                                    : "N/A"}
                                </InfoValue>
                              </InfoItem>
                            </InfoRow>
                          </InfoSection>

                          {/* Contact Information */}
                          <InfoSection>
                            <SectionTitle>Contact</SectionTitle>
                            <InfoRow>
                              <InfoItem>
                                <InfoLabel>Phone</InfoLabel>
                                <InfoValue>
                                  {selectedTenantData.phone ? (
                                    <PhoneLink
                                      href={`tel:${selectedTenantData.phone}`}
                                    >
                                      {formatPhoneNumber(
                                        selectedTenantData.phone
                                      )}
                                    </PhoneLink>
                                  ) : (
                                    "N/A"
                                  )}
                                </InfoValue>
                              </InfoItem>
                              <InfoItem>
                                <InfoLabel>Email</InfoLabel>
                                <InfoValue>
                                  {selectedTenantData.email || "N/A"}
                                </InfoValue>
                              </InfoItem>
                            </InfoRow>
                          </InfoSection>

                          {/* Lease Information */}
                          <InfoSection>
                            <SectionTitle>Lease</SectionTitle>
                            <InfoRow>
                              <InfoItem>
                                <InfoLabel>Status</InfoLabel>
                                <InfoValue status={selectedTenantData.status}>
                                  {selectedTenantData.status.toUpperCase() ||
                                    "N/A"}
                                </InfoValue>
                              </InfoItem>
                              <InfoItem>
                                <InfoLabel>Lease End</InfoLabel>
                                <InfoValue>
                                  {formatDate(selectedTenantData.leaseEnd)}
                                </InfoValue>
                              </InfoItem>
                            </InfoRow>
                          </InfoSection>
                        </DetailedInfo>
                      )}
                  </CompactTenantInfo>
                )}
              </ChatInfo>
            </ChatHeader>

            {selectedChat?._isPublicLeoSession && (
              <div style={{ width: "100%", paddingBottom: "0.35rem" }}>
                <Alert
                  type="info"
                  icon={
                    <InfoCircleOutlined style={{ fontSize: 14 }} aria-hidden />
                  }
                  message="Leo only replies to the tenant in this thread, not to you."
                  style={{
                    width: "100%",
                    padding: "5px 12px",
                    fontSize: 12,
                    lineHeight: 1.35,
                    marginBottom: 0,
                    borderRadius: 0,
                  }}
                />
              </div>
            )}

            {/* Messages */}
            <MessagesContainer ref={messagesContainerRef}>
              {isMessagesPanelLoading ? (
                <MessagesLoadingState>
                  <Spin size="large" tip="Loading messages…" />
                </MessagesLoadingState>
              ) : (
                <>
              {(() => {
                // Combine regular messages with pending (optimistic) messages
                const pendingMessagesArray = Array.from(
                  pendingMessages.values()
                )
                  .filter((pm) => pm.chatId === selectedChat?.id)
                  .map((pm) => pm.message);

                // Filter out optimistic messages that have been confirmed (matched by content and sender)
                // Match optimistic messages with real messages by content and sender
                const confirmedOptimisticIds = new Set();

                // For each optimistic message, check if there's a matching real message
                // Use a more aggressive matching strategy - if content matches and sender is landlord, remove optimistic
                pendingMessagesArray.forEach((optMsg) => {
                  if (!optMsg.isOptimistic) return;

                  const optContent =
                    typeof optMsg.content === "string"
                      ? optMsg.content.trim()
                      : "";
                  if (!optContent) return;

                  // Look for ANY matching real message from the landlord with the same content
                  // Don't require strict timestamp matching - if content matches, it's the same message
                  const matchingRealMsg = messages.find((realMsg) => {
                    // Must be from landlord
                    if (realMsg.sender !== "landlord") return false;

                    // Check fromUserId if available
                    if (realMsg.fromUserId && realMsg.fromUserId !== user?.uid)
                      return false;

                    // Extract content from real message - handle various formats
                    let realContent = "";
                    if (typeof realMsg.content === "string") {
                      realContent = realMsg.content.trim();
                    } else if (realMsg.content?.text) {
                      realContent = realMsg.content.text.trim();
                    } else if (realMsg.message) {
                      realContent =
                        typeof realMsg.message === "string"
                          ? realMsg.message.trim()
                          : realMsg.message.text?.trim() || "";
                    }

                    // Match by exact content (case-insensitive, whitespace-normalized)
                    if (!realContent) return false;

                    const contentMatches =
                      optContent.toLowerCase() === realContent.toLowerCase();

                    if (contentMatches) {
                      // Content matches - this is the same message
                      // Check timestamp to ensure it's recent (within 2 minutes to be very lenient)
                      const realTime =
                        realMsg.timestamp instanceof Date
                          ? realMsg.timestamp
                          : new Date(realMsg.timestamp);
                      const optTime =
                        optMsg.timestamp instanceof Date
                          ? optMsg.timestamp
                          : new Date(optMsg.timestamp);
                      const timeDiff = Math.abs(realTime - optTime);

                      // Very lenient time window - if content matches and it's within 2 minutes, it's a match
                      if (timeDiff < 120000) {
                        // 2 minutes
                        console.log(
                          "[Communication] Matching optimistic message to real message:",
                          {
                            optimisticId: optMsg.id,
                            optimisticContent: optContent,
                            realContent: realContent,
                            timeDiff: timeDiff,
                            realMsgId: realMsg.id,
                            realSender: realMsg.sender,
                            realFromUserId: realMsg.fromUserId,
                          }
                        );
                        return true;
                      }
                    }

                    return false;
                  });

                  if (matchingRealMsg) {
                    confirmedOptimisticIds.add(optMsg.id);
                  }
                });

                // Also check for duplicate messages - if we have both an optimistic and real message with same content,
                // remove the optimistic one immediately
                if (pendingMessagesArray.length > 0 && messages.length > 0) {
                  const recentLandlordMessages = messages.filter(
                    (msg) =>
                      msg.sender === "landlord" &&
                      (!msg.fromUserId || msg.fromUserId === user?.uid)
                  );

                  pendingMessagesArray.forEach((optMsg) => {
                    if (
                      !optMsg.isOptimistic ||
                      confirmedOptimisticIds.has(optMsg.id)
                    )
                      return;

                    const optContent =
                      typeof optMsg.content === "string"
                        ? optMsg.content.trim()
                        : "";
                    if (!optContent) return;

                    // Check if any recent landlord message has the same content
                    const hasDuplicate = recentLandlordMessages.some(
                      (realMsg) => {
                        let realContent = "";
                        if (typeof realMsg.content === "string") {
                          realContent = realMsg.content.trim();
                        } else if (realMsg.content?.text) {
                          realContent = realMsg.content.text.trim();
                        } else if (realMsg.message) {
                          realContent =
                            typeof realMsg.message === "string"
                              ? realMsg.message.trim()
                              : realMsg.message.text?.trim() || "";
                        }

                        return (
                          realContent &&
                          optContent.toLowerCase() === realContent.toLowerCase()
                        );
                      }
                    );

                    if (hasDuplicate) {
                      console.log(
                        "[Communication] Found duplicate message - removing optimistic:",
                        optMsg.id,
                        optContent
                      );
                      confirmedOptimisticIds.add(optMsg.id);
                    }
                  });
                }

                // Filter out confirmed optimistic messages - these should NOT appear in the UI
                // They've been matched to real messages, so we only show the real ones
                const activePendingMessages = pendingMessagesArray.filter(
                  (msg) => {
                    const isConfirmed = confirmedOptimisticIds.has(msg.id);
                    if (isConfirmed && msg.isOptimistic) {
                      console.log(
                        "[Communication] Filtering out confirmed optimistic message from display:",
                        msg.id,
                        msg.content
                      );
                    }
                    return !isConfirmed;
                  }
                );

                // Merge pending messages with regular messages
                // Optimistic messages should always appear at the end (most recent), not sorted by timestamp
                // This prevents them from appearing in the wrong position
                const allMessages = [...messages];

                // Add only unconfirmed optimistic messages at the end (they're the most recent)
                activePendingMessages.forEach((optMsg) => {
                  if (optMsg.isOptimistic) {
                    // Add optimistic messages at the end, sorted by their creation order
                    allMessages.push(optMsg);
                  } else {
                    // Non-optimistic pending messages should be sorted normally
                    allMessages.push(optMsg);
                  }
                });

                // Sort all messages by timestamp, but keep optimistic messages at the end
                allMessages.sort((a, b) => {
                  // If one is optimistic and the other isn't, optimistic always comes after
                  if (a.isOptimistic && !b.isOptimistic) return 1;
                  if (!a.isOptimistic && b.isOptimistic) return -1;

                  // Both are the same type, sort by timestamp
                  const aTime =
                    a.timestamp instanceof Date
                      ? a.timestamp
                      : new Date(a.timestamp);
                  const bTime =
                    b.timestamp instanceof Date
                      ? b.timestamp
                      : new Date(b.timestamp);
                  return aTime - bTime;
                });

                // Clean up confirmed optimistic messages from pendingMessages state immediately
                if (confirmedOptimisticIds.size > 0) {
                  // Remove immediately, don't wait
                  setPendingMessages((prev) => {
                    const newMap = new Map(prev);
                    let removedAny = false;
                    confirmedOptimisticIds.forEach((id) => {
                      if (newMap.has(id)) {
                        console.log(
                          "[Communication] Removing confirmed optimistic message:",
                          id
                        );
                        newMap.delete(id);
                        removedAny = true;
                      }
                    });
                    return removedAny ? newMap : prev; // Only update if we actually removed something
                  });
                }

                return allMessages.map((message) => {
                  // Check if this message is related to a repair ticket
                  const repairTicketId =
                    message.metadata?.repairTicketId || message.repairTicketId;

                  // Handle different possible image field names and nested structures
                  // Images can be at: message.images, message.content.images, message.message.images, message.imageUrls, message.attachments
                  let messageImages = [];
                  if (message.images && Array.isArray(message.images)) {
                    messageImages = message.images;
                  } else if (
                    message.content?.images &&
                    Array.isArray(message.content.images)
                  ) {
                    messageImages = message.content.images;
                  } else if (
                    message.message?.images &&
                    Array.isArray(message.message.images)
                  ) {
                    messageImages = message.message.images;
                  } else if (
                    message.imageUrls &&
                    Array.isArray(message.imageUrls)
                  ) {
                    messageImages = message.imageUrls;
                  } else if (
                    message.attachments &&
                    Array.isArray(message.attachments)
                  ) {
                    messageImages = message.attachments;
                  }

                  return (
                    <MessageBubble
                      key={message.id}
                      sender={message.sender}
                      data-message-id={message.id}
                      data-repair-ticket-id={repairTicketId || undefined}
                    >
                      <MessageAvatar sender={message.sender}>
                        <Avatar
                          size="small"
                          icon={getSenderIcon(message.sender)}
                          style={{
                            backgroundColor:
                              message.sender === "ai"
                                ? "var(--color-primary)"
                                : message.sender === "landlord"
                                ? "var(--color-primary)"
                                : message.sender === "vendor"
                                ? "#52c41a"
                                : "#1890ff",
                          }}
                        />
                      </MessageAvatar>
                      <MessageContent sender={message.sender}>
                        <MessageHeader sender={message.sender}>
                          {message.sender === "landlord" ? (
                            <>
                              <MessageTime>
                                {formatTime(message.timestamp)}
                              </MessageTime>
                              <MessageSender>
                                {getSenderName(message.sender, message)}
                              </MessageSender>
                              {message.isSending && (
                                <MessageSendingSpinner>
                                  <LoadingOutlined
                                    style={{
                                      fontSize: "12px",
                                      color: "rgba(255, 255, 255, 0.7)",
                                    }}
                                  />
                                </MessageSendingSpinner>
                              )}
                            </>
                          ) : (
                            <>
                              <MessageSender>
                                {getSenderName(message.sender, message)}
                              </MessageSender>
                              <MessageTime>
                                {formatTime(message.timestamp)}
                              </MessageTime>
                            </>
                          )}
                        </MessageHeader>
                        {message.content && String(message.content).trim() && (
                          <MessageText
                            sender={message.sender}
                            $isMobile={isMobile}
                            $hasImages={messageImages.length > 0}
                          >
                            {renderLeoMarkdown(message.content)}
                          </MessageText>
                        )}
                        {/* Display images if present */}
                        {messageImages.length > 0 && (
                          <MessageImages>
                            {messageImages.map((imageUrl, idx) => {
                              // Handle both string URLs and objects with url property
                              const url =
                                typeof imageUrl === "string"
                                  ? imageUrl
                                  : imageUrl.url || imageUrl.src;
                              return (
                                <MessageImage
                                  key={idx}
                                  src={url}
                                  alt={`Attachment ${idx + 1}`}
                                  onClick={() => window.open(url, "_blank")}
                                  onError={(e) => {
                                    console.error(
                                      "[Communication] Failed to load image:",
                                      url,
                                      e
                                    );
                                  }}
                                />
                              );
                            })}
                          </MessageImages>
                        )}
                      </MessageContent>
                    </MessageBubble>
                  );
                });
              })()}
              <div ref={messagesEndRef} />
                </>
              )}
            </MessagesContainer>

            {/* Input Area */}
            <InputArea>
              <input
                ref={publicLeoFileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handlePublicLeoComposeAttach}
              />
              {selectedChat?._isPublicLeoSession &&
                publicLeoPendingImages.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      padding: "0 1rem 8px",
                      alignItems: "center",
                    }}
                  >
                    {publicLeoPendingImages.map((img, idx) => (
                      <div
                        key={`${img.url}-${idx}`}
                        style={{ position: "relative", width: 56, height: 56 }}
                      >
                        <img
                          src={img.previewUrl}
                          alt=""
                          style={{
                            width: 56,
                            height: 56,
                            objectFit: "cover",
                            borderRadius: 8,
                            border: "1px solid #e5e7eb",
                          }}
                        />
                        <Button
                          size="small"
                          type="text"
                          icon={<CloseOutlined />}
                          onClick={() => removePublicLeoPendingImage(idx)}
                          style={{
                            position: "absolute",
                            top: -10,
                            right: -10,
                            minWidth: 24,
                            height: 24,
                            padding: 0,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              <InputContainer>
                {selectedChat?._isPublicLeoSession && (
                  <AttachButton
                    type="text"
                    icon={<PaperClipOutlined />}
                    loading={publicLeoImageUploading}
                    onClick={() => publicLeoFileInputRef.current?.click()}
                    aria-label="Attach images"
                    $active={false}
                    style={{ flexShrink: 0, marginRight: 4 }}
                  />
                )}
                <TextArea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={
                    selectedChat?._isPublicLeoSession
                      ? "Message the tenant..."
                      : `Message ${selectedChat?.tenantName || "tenant"}...`
                  }
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  disabled={false}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <SendButton
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  loading={isComposing}
                  disabled={
                    selectedChat?._isPublicLeoSession
                      ? (!newMessage.trim() &&
                          publicLeoPendingImages.length === 0) ||
                        publicLeoImageUploading
                      : !newMessage.trim()
                  }
                >
                  {!isMobile && "Send"}
                </SendButton>
              </InputContainer>
              <Disclaimer>
                <DisclaimerText>
                  Leo can make mistakes. Check important info.
                </DisclaimerText>
              </Disclaimer>
            </InputArea>
          </>
        ) : (
          <EmptyState>
            <EmptyStateTitle>Select a conversation</EmptyStateTitle>
            <EmptyStateSubtitle>
              {isOpsOnlyPlan
                ? "Pick a thread from the list, or go to Settings → Tenant chat to turn on a door and share the magic link."
                : "Choose a tenant from the sidebar to start messaging."}
              {!isMobile && <br />}
              {isMobile && " "}
              {!isOpsOnlyPlan &&
                "Each tenant on your rent roll can have a thread here."}
            </EmptyStateSubtitle>
            {isMobile && (
              <EmptyStateAction>
                <Button
                  type="primary"
                  icon={
                    <MessageOutlined
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                  }
                  onClick={handleSidebarToggle}
                  size="large"
                >
                  View All Conversations
                </Button>
              </EmptyStateAction>
            )}
          </EmptyState>
        )}
      </MainChatArea>
      <HealthConsentModal
        open={healthConsentModalVisible}
        onAccept={() => setHealthConsentModalVisible(false)}
        onCancel={() => setHealthConsentModalVisible(false)}
      />
    </ChatContainer>
  );
};

// Styled Components
// Mobile-specific components
const MobileHeader = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  border-bottom: 2px solid #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  z-index: 1001;
  flex-direction: column;

  @media ${device.mobile} {
    display: flex;
  }
`;

const MobileHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const MobileHeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const MobileBackButton = styled(Button)`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(100, 116, 139, 0.25);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(100, 116, 139, 0.35);
    background: linear-gradient(135deg, #475569 0%, #334155 100%);
  }

  .anticon {
    font-size: 1.2rem;
  }
`;

const MobileHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const MobileBackArrow = styled(Button)`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: transparent;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(100, 116, 139, 0.1);
    color: #475569;
  }

  .anticon {
    font-size: 1.2rem;
  }
`;

const MobileCenterText = styled.div`
  font-size: 1rem;
  color: #1e293b;
  font-weight: 700;
  letter-spacing: 0.025em;
  text-align: center;
  flex: 1;
  margin: 0 1rem;
  padding: 0.75rem 0;
`;

const MobileToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.25rem;
  margin: 0 1rem;
  margin-bottom: 0.75rem; /* add space below selector on mobile */
  border-top: 1px solid rgba(203, 213, 225, 0.5);
  background: rgba(44, 95, 45, 0.03);
  border-radius: 8px;
`;

const MobileToggleButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 6px;
  background: ${(props) =>
    props.$active ? "var(--color-primary)" : "transparent"};
  color: ${(props) => (props.$active ? "#ffffff" : "#64748b")};
  font-size: 0.85rem;
  font-weight: ${(props) => (props.$active ? "700" : "600")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.$active ? "var(--color-primary)" : "rgba(44, 95, 45, 0.1)"};
    color: ${(props) => (props.$active ? "#ffffff" : "var(--color-primary)")};
  }

  .anticon {
    font-size: 1rem;
    color: inherit;
    transition: color 0.2s ease;
  }
  .anticon svg {
    fill: currentColor;
    stroke: currentColor;
    transition: filter 0.2s ease;
  }

  ${(props) =>
    props.$active &&
    `
    .anticon { color: #ffffff !important; }
    .anticon svg { filter: brightness(0) invert(1) !important; }
  `}

  /* Icon-only on mobile: hide non-icon spans */
  span:not(.anticon) {
    display: none;
  }
`;

const MobileMenuButton = styled(Button)`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: transparent;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(100, 116, 139, 0.1);
    color: #475569;
  }

  .anticon {
    font-size: 1.2rem;
  }
`;

const MobileLogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MobileLogoIcon = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
`;

const MobileTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  letter-spacing: 0.025em;
`;

const MobileOverlay = styled.div`
  display: none;
  position: fixed;
  top: 120px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media ${device.mobile} {
    display: block;
  }
`;

const ChatContainer = styled.div`
  display: flex;
  height: 100vh; /* Full viewport height since no navbar/footer */
  background: #ffffff;
  overflow: hidden; /* Prevent page scrolling */

  @media ${device.mobile} {
    padding-top: 50px; /* Account for mobile header with toggle */
  }
`;

const Sidebar = styled.div`
  width: 340px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
  border-right: 2px solid #cbd5e1;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.08);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--color-primary) 0%,
      #2d5a2d 50%,
      var(--color-primary) 100%
    );
    z-index: 10;
  }

  /* Cover the border-right at the top where the green bar is */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: -2px;
    width: 2px;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--color-primary) 0%,
      #2d5a2d 50%,
      var(--color-primary) 100%
    );
    z-index: 11;
  }

  @media ${device.mobile} {
    width: 100%;
    position: fixed;
    top: 50px;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    height: calc(100vh - 50px);
    transform: ${(props) =>
      props.$isOpen ? "translateX(0)" : "translateX(100%)"};
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: -8px 0 32px rgba(0, 0, 0, 0.15);
  }
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 2px solid #cbd5e1;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--color-primary) 50%,
      transparent 100%
    );
  }

  @media ${device.mobile} {
    display: none; /* Hide on mobile since we have mobile header */
  }
`;

const SidebarBackButton = styled(Button)`
  width: 100%;
  height: 48px;
  border-radius: 16px;
  font-weight: 700;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    #2d5a2d 50%,
    #1a3d1a 100%
  );
  border: none;
  box-shadow: 0 6px 20px rgba(44, 95, 45, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(44, 95, 45, 0.4);
    background: linear-gradient(
      135deg,
      #2d5a2d 0%,
      #1a3d1a 50%,
      var(--color-primary) 100%
    );

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 15px rgba(44, 95, 45, 0.3);
  }

  .anticon {
    font-size: 1.1rem;
    z-index: 1;
  }
`;

const SidebarHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`;

const SidebarBackArrow = styled(Button)`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: transparent;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-right: 0;
  margin-bottom: 0;

  &:hover {
    background: rgba(100, 116, 139, 0.1);
    color: #475569;
  }

  .anticon {
    font-size: 1.2rem;
  }
`;

const SidebarLogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0;
`;

const SidebarTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: 0.025em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const SidebarSubtitle = styled.div`
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 600;
  letter-spacing: 0.025em;
  margin-bottom: 0;
  margin: auto;
  margin-left: 0;

  @media ${device.mobile} {
    margin: auto;
  }
`;

const ChatTypeToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 1rem;
  padding: 0.25rem;
  background: rgba(44, 95, 45, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(44, 95, 45, 0.1);
`;

const ToggleButton = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.45rem 0.6rem;
  border: none;
  border-radius: 8px;
  background: ${(props) =>
    props.$active ? "var(--color-primary)" : "transparent"};
  color: ${(props) => (props.$active ? "#ffffff" : "#64748b")};
  font-size: 0.75rem;
  font-weight: ${(props) => (props.$active ? "700" : "600")};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 0;

  &:hover {
    background: ${(props) =>
      props.$active ? "var(--color-primary)" : "rgba(44, 95, 45, 0.1)"};
    color: ${(props) => (props.$active ? "#ffffff" : "var(--color-primary)")};
  }

  .anticon {
    font-size: 1.3rem;
    line-height: 1;
    color: inherit;
    transition: color 0.2s ease;
  }
  .anticon svg {
    fill: currentColor;
    stroke: currentColor;
    transition: filter 0.2s ease, fill 0.2s ease, stroke 0.2s ease;
  }

  ${(props) =>
    props.$active &&
    `
    .anticon { color: #ffffff !important; }
    .anticon svg { filter: brightness(0) invert(1) !important; }
  `}

  /* Hide only the text label spans, not the icon span */
  span:not(.anticon) {
    display: none;
  }
`;

const SidebarLogoIcon = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-weight: 800;
  font-size: 1.5rem;
  color: var(--color-primary);
  margin-bottom: 0.75rem;
  position: relative;

  .anticon {
    font-size: 2rem;
    filter: drop-shadow(0 4px 8px rgba(44, 95, 45, 0.3));
    background: linear-gradient(135deg, var(--color-primary) 0%, #2d5a2d 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -4px;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      var(--color-primary) 0%,
      transparent 100%
    );
    border-radius: 1px;
  }
`;

const HeaderSubtitle = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 0;
  letter-spacing: 0.025em;
`;

const ChatList = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.75rem;
  min-height: 0;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #cbd5e1 0%, #94a3b8 100%);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
  }
`;

const ChatItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
  background: ${(props) =>
    props.$hasError
      ? "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)"
      : "linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)"};
  border: 1px solid ${(props) => (props.$hasError ? "#cbd5e1" : "#e2e8f0")};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  opacity: ${(props) => (props.$hasError ? 0.9 : 1)};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(props) =>
      props.$hasError
        ? "linear-gradient(90deg, #64748b 0%, #475569 50%, #334155 100%)"
        : "linear-gradient(90deg, var(--color-primary) 0%, #2d5a2d 50%, var(--color-primary) 100%)"};
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.6s ease;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border-color: ${(props) => (props.$hasError ? "#94a3b8" : "#cbd5e1")};

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
    transition: all 0.1s ease;
  }

  &.active {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%);
    border-color: var(--color-primary);
    box-shadow: 0 4px 16px rgba(44, 95, 45, 0.15);
    transform: translateY(-1px);

    &::before {
      opacity: 1;
    }
  }

  @media ${device.mobile} {
    padding: 0.75rem;
    margin-bottom: 0.5rem;
    border-radius: 12px;
    min-height: 60px;

    &:active {
      transform: scale(0.98);
      background: ${(props) =>
        props.$hasError
          ? "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)"
          : "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)"};
    }
  }
`;

const ChatAvatar = styled.div`
  position: relative;
  margin-right: 0.75rem;
  flex-shrink: 0;

  .ant-avatar {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    border: 3px solid #ffffff;
    transition: all 0.3s ease;
  }

  &:hover .ant-avatar {
    transform: scale(1.1);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
`;

const UnreadBadge = styled(Badge)`
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 10;

  .ant-badge-count {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
    font-weight: 700;
    font-size: 0.8rem;
    border: 2px solid #ffffff;
  }
`;

const ChatContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChatName = styled.div`
  font-weight: 600;
  color: ${(props) => (props.$hasError ? "#64748b" : "#1e293b")};
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
  letter-spacing: 0.025em;

  @media ${device.mobile} {
    font-size: 1rem;
    margin-bottom: 0.25rem;
  }
`;

const ChatUnit = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  margin-bottom: 0.25rem;
  font-weight: 500;
  letter-spacing: 0.025em;

  @media ${device.mobile} {
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
  }
`;

const ChatPreview = styled.div`
  font-size: 0.8rem;
  color: ${(props) => (props.$hasError ? "#64748b" : "#64748b")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: ${(props) => (props.$hasError ? "500" : "400")};
  font-style: ${(props) => (props.$hasError ? "italic" : "normal")};

  @media ${device.mobile} {
    font-size: 0.85rem;
  }
`;

const ChatTime = styled.div`
  font-size: 0.7rem;
  color: #94a3b8;
  margin-left: 0.75rem;
  font-weight: 500;
  flex-shrink: 0;
  letter-spacing: 0.025em;
`;

const SidebarFooter = styled.div`
  padding: 2rem 1.5rem;
  border-top: 2px solid #cbd5e1;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
`;

const SidebarStats = styled.div`
  display: flex;
  gap: 0.375rem;
  margin-bottom: 1rem;

  @media ${device.mobile} {
    flex-direction: row;
    gap: 0.25rem;
    flex-wrap: wrap;
    justify-content: space-between;
  }
`;

const StatItem = styled.div`
  flex: 1;
  padding: 0.5rem 0.375rem;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  min-height: 45px;
  display: flex;
  align-items: center;
  gap: 0.375rem;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }
`;

const StatValue = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--color-primary);
  margin-bottom: 0.125rem;
`;

const StatLabel = styled.div`
  font-size: 0.55rem;
  color: #64748b;
  font-weight: 500;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  line-height: 1.1;
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--color-primary);
  font-size: 0.875rem;
  flex-shrink: 0;
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  flex: 1;
`;

const MainChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(
    135deg,
    #f8fafc 0%,
    #e2e8f0 100%
  ); /* Light gray background for sidebars, matching tenant/vendor chats */
  height: 100%;
  overflow: hidden;
  position: relative;

  /* Center the chat content with max-width */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--color-primary) 0%,
      #2d5a2d 50%,
      var(--color-primary) 100%
    );
    z-index: 10;
  }

  /* Cover the border-right at the top where the green bar is */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: -2px;
    width: 2px;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--color-primary) 0%,
      #2d5a2d 50%,
      var(--color-primary) 100%
    );
    z-index: 11;
  }

  @media ${device.mobile} {
    height: calc(100vh - 50px);

    &::before {
      display: none; /* Hide accent bar on mobile */
    }
  }
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 2px solid #cbd5e1;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--color-primary) 50%,
      transparent 100%
    );
  }

  @media ${device.mobile} {
    padding: 0.5rem 1rem;
    border-bottom: 1px solid #cbd5e1;

    &::after {
      display: none; /* Hide accent line on mobile */
    }
  }
`;

const ChatInfo = styled.div`
  flex: 1;
`;

const ChatTitle = styled.div`
  font-weight: 800;
  font-size: 1.25rem;
  color: #1e293b;
  margin-bottom: 0.25rem;
  letter-spacing: 0.025em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  @media ${device.mobile} {
    font-size: 1.1rem;
    margin-bottom: 0.25rem;
  }
`;

const ChatSubtitle = styled.div`
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 600;
  letter-spacing: 0.025em;

  @media ${device.mobile} {
    font-size: 0.8rem;
  }
`;

const NotificationSettings = styled.div`
  margin-top: 0;
  display: flex;
  justify-content: flex-end;

  @media ${device.mobile} {
    margin-top: 0;
  }
`;

const NotificationButton = styled.button`
  background: rgba(44, 95, 45, 0.1);
  border: 1px solid rgba(44, 95, 45, 0.2);
  color: var(--color-primary);
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(44, 95, 45, 0.2);
    border-color: rgba(44, 95, 45, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  @media ${device.mobile} {
    padding: 0.2rem 0.5rem;
    font-size: 0.7rem;
  }
`;

const NotificationStatus = styled.div`
  color: #64748b;
  font-size: 0.75rem;
  padding: 0.25rem 0.75rem;
  background: rgba(100, 116, 139, 0.1);
  border-radius: 6px;

  span {
    font-size: 17px !important;
  }

  @media ${device.mobile} {
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
  }
`;

const CompactTenantInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 0px;
  position: relative;

  @media ${device.mobile} {
    gap: 0.25rem;
    margin-top: 0;
    padding-top: 0;
  }
`;

const InfoRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;

  @media ${device.mobile} {
    gap: 0.5rem;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;

  @media ${device.mobile} {
    gap: 0.05rem;
  }
`;

const InfoLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.025em;

  @media ${device.mobile} {
    font-size: 0.55rem;
    letter-spacing: 0.015em;
  }
`;

const InfoValue = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.3;

  @media ${device.mobile} {
    font-size: 0.65rem;
    line-height: 1.1;
  }

  ${(props) =>
    props.status === "active" &&
    `
    color: #059669;
    font-weight: 600;
  `}
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem 1.5rem;
  min-height: 0; /* Allow flex item to shrink below content size */
  max-width: 768px;
  margin: 0 auto;
  width: 100%;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-primary);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--color-primary-dark);
  }
`;

const MessagesLoadingState = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  width: 100%;
`;

const MessageBubble = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  align-items: flex-start;
  justify-content: ${(props) =>
    props.sender === "landlord" ? "flex-end" : "flex-start"};

  &:last-child {
    margin-bottom: 0;
  }
`;

const MessageAvatar = styled.div`
  margin-right: ${(props) => (props.sender === "landlord" ? "0" : "0.75rem")};
  margin-left: ${(props) => (props.sender === "landlord" ? "0.75rem" : "0")};
  flex-shrink: 0;
  order: ${(props) => (props.sender === "landlord" ? "2" : "1")};

  span {
    padding: 2px;
  }
`;

const MessageContent = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 70%;
  order: ${(props) => (props.sender === "landlord" ? "1" : "2")};
  display: flex;
  flex-direction: column;
  align-items: ${(props) =>
    props.sender === "landlord" ? "flex-end" : "flex-start"};
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: ${(props) =>
    props.sender === "landlord" ? "flex-end" : "flex-start"};
  align-items: center;
  margin-bottom: 0.25rem;
  width: 100%;
  gap: 0.5rem;
`;

const MessageSender = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
`;

const MessageTime = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const MessageSendingSpinner = styled.span`
  margin-left: 0.5rem;
  display: inline-flex;
  align-items: center;
  opacity: 0.7;
`;

const MessageText = styled.div`
  color: ${(props) => (props.sender === "landlord" ? "#ffffff" : "#1f2937")};
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: ${(props) =>
    props.sender === "landlord"
      ? "var(--color-primary)"
      : props.sender === "ai"
      ? "rgba(44, 95, 45, 0.05)"
      : "#f8f9fa"};
  border: ${(props) =>
    props.sender === "landlord"
      ? "none"
      : props.sender === "ai"
      ? "1px solid rgba(44, 95, 45, 0.1)"
      : "1px solid #e9ecef"};
  box-shadow: ${(props) =>
    props.sender === "landlord"
      ? "0 2px 8px rgba(44, 95, 45, 0.2)"
      : props.sender === "ai"
      ? "0 2px 8px rgba(44, 95, 45, 0.1)"
      : "0 1px 3px rgba(0, 0, 0, 0.1)"};
  font-size: 0.95rem;
  text-align: left;
  width: fit-content;
  max-width: ${(props) => (props.sender === "landlord" ? "500px" : "95%")};
  margin-bottom: ${(props) => (props.$hasImages ? "0.5rem" : "0")};

  @media ${device.mobile} {
    max-width: 95%;
  }
`;

const MessageImages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  max-width: 300px;
`;

const MessageImage = styled.img`
  width: 100%;
  max-width: 300px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid rgba(44, 95, 45, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: var(--color-primary);
  }
`;

const InputArea = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e5e5;
  background: #ffffff;

  @media ${device.mobile} {
    padding: 0.75rem 1rem;
  }
`;

const InputContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  max-width: 768px;
  margin: 0 auto;
  width: 100%;

  .ant-input {
    border: 2px solid #e5e5e5 !important;
    border-radius: 12px !important;
    padding: 0.75rem 1rem !important;
    font-size: 0.95rem !important;
    transition: all 0.2s ease !important;
    background: #ffffff !important;
    min-height: 48px !important;
    resize: none !important;

    &:focus,
    &:hover {
      border-color: var(--color-primary) !important;
      box-shadow: 0 0 0 2px rgba(44, 95, 45, 0.1) !important;
    }
  }
`;

const Disclaimer = styled.div`
  margin: 0.25rem 0 0 0;
  text-align: center;
  padding: 0 0.5rem;

  @media ${device.mobile} {
    margin: 0.5rem 0 0 0;
    padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
  }
`;

const DisclaimerText = styled.div`
  font-size: 0.75rem;
  color: #000000;
  line-height: 1.4;
  font-weight: 400;
  opacity: 0.7;
  letter-spacing: 0.2px;

  @media ${device.mobile} {
    font-size: 0.7rem;
  }
`;

const SendButton = styled(Button)`
  height: 100%;
  min-height: 48px;
  border-radius: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  min-height: -webkit-fill-available;

  .ant-btn-icon {
    filter: brightness(0) invert(1);
  }

  &:disabled {
    background: #d1d5db !important;
    border-color: #d1d5db !important;
    color: #9ca3af !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;

    .ant-btn-icon {
      filter: brightness(0) invert(0.5) !important;
    }
  }

  @media ${device.mobile} {
    width: 54px !important;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 3rem 2rem;
  overflow: hidden;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
`;

const EmptyStateIcon = styled.div`
  font-size: 4rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyStateTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  letter-spacing: -0.01em;
  line-height: 1.3;
  font-family: var(
    --font-primary,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    "Roboto",
    sans-serif
  );
`;

const EmptyStateSubtitle = styled.p`
  font-size: 1.125rem;
  color: #64748b;
  margin-bottom: 1.5rem;
  max-width: 600px;
  line-height: 1.6;
  font-weight: 500;
`;

const EmptyStateStats = styled.div`
  display: flex;
  gap: 2rem;

  @media ${device.mobile} {
    flex-direction: column;
    gap: 1rem;
  }
`;

const EmptyStateAction = styled.div`
  margin-top: 0;
  display: flex;
  justify-content: center;

  .ant-btn {
    background: linear-gradient(135deg, #2c5f2d 0%, #4a7c59 100%) !important;
    border: none !important;
    border-radius: 12px !important;
    height: 48px !important;
    padding: 0 24px !important;
    font-weight: 600 !important;
    font-size: 1rem !important;
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.25) !important;
    transition: all 0.3s ease !important;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(44, 95, 45, 0.35) !important;
      background: linear-gradient(135deg, #4a7c59 0%, #2c5f2d 100%) !important;
    }

    .anticon {
      color: white !important;
      filter: brightness(0) invert(1);
    }

    span {
      color: white !important;
    }

    @media ${device.mobile} {
      height: 44px !important;
      font-size: 0.95rem !important;
    }
  }
`;

// Loading and Empty State Components
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 1rem;
`;

const LoadingText = styled.div`
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 500;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 0.75rem;
`;

const EmptyText = styled.div`
  color: #374151;
  font-size: 1rem;
  font-weight: 600;
`;

const EmptySubtext = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
  text-align: center;
  line-height: 1.4;
`;

// New styled components for expandable tenant info
const EssentialInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;

  @media (min-width: 769px) {
    flex-direction: row;
    align-items: center;
    gap: 1.5rem;
    flex-wrap: wrap;
  }
`;

const TenantName = styled.div`
  font-weight: 700;
  font-size: 1.1rem;
  color: #1e293b;

  @media ${device.mobile} {
    font-size: 1rem;
  }
`;

const TenantAddress = styled.div`
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;

  @media ${device.mobile} {
    font-size: 0.8rem;
  }
`;

const ParticipantsList = styled.div`
  margin-top: 0.25rem;
  padding-top: 0.25rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  width: 100%;
  flex-shrink: 0;

  @media ${device.mobile} {
    margin-top: 0.25rem;
    padding-top: 0.25rem;
  }

  @media (min-width: 769px) {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
    border-left: 1px solid rgba(0, 0, 0, 0.06);
    padding-left: 1rem;
    margin-left: 1rem;
    width: auto;
    min-width: 0;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
  }
`;

const ParticipantItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  width: 100%;
`;

const ParticipantTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  width: 100%;
  align-items: center;
  align-content: flex-start;
`;

const ParticipantIconTag = styled.span`
  background: transparent;
  color: var(--color-primary);
  padding: 0.25rem 0;
  border-radius: 12px;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: 0.125rem;
  height: 24px;
  box-sizing: border-box;

  svg {
    font-size: 0.75rem;
  }
`;

const ParticipantTag = styled.span`
  background: rgba(44, 95, 45, 0.1);
  color: var(--color-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(44, 95, 45, 0.2);
  white-space: nowrap;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  line-height: 1.2;
  height: 24px;
  box-sizing: border-box;
`;

const ParticipantTagWithToggle = styled.span`
  background: rgba(44, 95, 45, 0.1);
  color: var(--color-primary);
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(44, 95, 45, 0.2);
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.2;
  height: 24px;
  box-sizing: border-box;

  /* Ensure icons and text don't break */
  > svg {
    flex-shrink: 0;
    font-size: 0.7rem;
  }

  > span {
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 0.75rem;
  }

  /* Ensure toggle switch doesn't break layout */
  .ant-switch {
    flex-shrink: 0;
    margin: 0;
  }

  /* Badge styling */
  .ant-badge {
    flex-shrink: 0;
  }
`;

const LeoToggle = styled(Switch)`
  && {
    min-width: 32px;
    height: 18px;

    .ant-switch-handle {
      width: 14px;
      height: 14px;
    }
  }
`;

const DetailedInfoToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  padding: 0.5rem 0;
  cursor: pointer;
  color: var(--color-primary);
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover {
    color: var(--color-primary-dark);
  }

  @media ${device.mobile} {
    font-size: 0.75rem;
    padding: 0.25rem 0;
  }
`;

const InfoIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;

  ${(props) =>
    props.$expanded &&
    `
    transform: rotate(180deg);
  `}

  @media ${device.mobile} {
    width: 14px;
    height: 14px;
  }
`;

const ToggleText = styled.span`
  font-size: inherit;
  font-weight: inherit;
`;

const DetailedInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(44, 95, 45, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(44, 95, 45, 0.1);
  max-width: 400px;

  @media (min-width: 769px) {
    flex-direction: row;
    gap: 1.5rem;
    max-width: 100%;
    padding: 1rem 1.5rem;
    margin-top: 0.75rem;
  }

  @media ${device.mobile} {
    gap: 0.1rem;
    margin-top: 0.25rem;
    padding: 0.25rem;
    max-width: 100%;
  }
`;

const ActionButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);

  @media (min-width: 769px) {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
    border-left: 1px solid rgba(0, 0, 0, 0.06);
    padding-left: 1rem;
    margin-left: 1rem;
    flex: 0 0 auto; /* Don't grow, don't shrink, auto width */
    justify-content: flex-end;
    gap: 1rem;
  }

  @media ${device.mobile} {
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    position: static;
  }
`;

const InfoSection = styled.div`
  margin-bottom: 1rem;
  flex: 1;

  &:last-child {
    margin-bottom: 0;
  }

  @media (min-width: 769px) {
    margin-bottom: 0;
    padding-right: 1.5rem;
    border-right: 1px solid rgba(44, 95, 45, 0.15);

    &:last-child {
      padding-right: 0;
      border-right: none;
    }
  }

  @media ${device.mobile} {
    margin-bottom: 0.75rem;
  }
`;

const SectionTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--color-primary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid rgba(44, 95, 45, 0.2);

  @media ${device.mobile} {
    font-size: 0.65rem;
    margin-bottom: 0.375rem;
    padding-bottom: 0.2rem;
  }
`;

const LogoImage = styled.img`
  width: 12px;
  height: 12px;
  object-fit: contain;
  filter: brightness(0) invert(1);
`;

const PhoneLink = styled.a`
  color: var(--color-primary);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    color: var(--color-primary-dark);
    text-decoration: underline;
  }

  &:active {
    color: var(--color-primary-dark);
  }
`;

export default Communication;
